import React, { useMemo } from 'react';
import {
    applyTableFilters,
    applyTableSort
} from '../byor/utils/tableFilterUtils';
import { DataTableWidget } from './DataTableWidget';
import { getLabelStyle } from './widgetLabelHelpers';
import './ProofTablesWidgets.css';
import './FindingsWidgets.css';

// Helper function to calculate pagination slice indices
// Supports different itemsPerPage for first page vs continuation pages
const getPaginationSlice = (pagination) => {
  if (!pagination) return null;
  const startIndex = pagination.startIndex !== undefined
    ? pagination.startIndex
    : pagination.currentPage * pagination.itemsPerPage;
  const endIndex = startIndex + pagination.itemsPerPage;
  return { startIndex, endIndex };
};

/**
 * Helper to apply hiddenColumns from config to show* booleans.
 * Takes config and a mapping of { columnHeaderName: showKey }.
 * Returns an object of overridden show* values.
 * Example: applyHiddenColumns(config, { 'No.': 'showNo', 'Username': 'showUsername' })
 * If 'No.' is in config.hiddenColumns, returns { showNo: false }
 */

export const getDisplayHeader = (config, headerName) => {
  return config?.customColumnHeaders?.[headerName] || headerName;
};

const applyHiddenColumns = (config, columnMap) => {
  const hiddenColumns = config?.hiddenColumns || [];
  if (hiddenColumns.length === 0) return {};
  const overrides = {};
  for (const [colName, showKey] of Object.entries(columnMap)) {
    if (hiddenColumns.includes(colName)) {
      overrides[showKey] = false;
    }
  }
  return overrides;
};

/**
 * =============================================================================
 * PROOF TABLES WIDGETS - COMPLETE IMPLEMENTATION
 * =============================================================================
 *
 * This file contains ALL proof table widgets from template2 backend files:
 * - Active Directory (AD.js): 14 tables
 * - Network (Network.js): 48 tables
 * - Localhost (Localhost.js): 32 tables
 * - Microsoft 365 (MS.js): 6 tables
 * - HIBP/Dark Web (HIBP.js): 4 tables
 * - PII (PII.js): 3 tables
 *
 * TOTAL: 107 proof table widgets
 *
 * Each widget uses the generic DataTableWidget component with a custom
 * dataExtractor function to pull data from the scan results.
 */

// =============================================================================
// ACTIVE DIRECTORY PROOF TABLES (14 widgets)
// =============================================================================

/**
 * AD Users - All Users Table
 * Shows all Active Directory user accounts
 */
/**
 * Extract AD Users data
 */
export function extractADAllUsersData(data) {
  const usersData = [];
  if (!data) return usersData;

  const scanDataSource = data?.scanData || data?.data || data;
  const adData = scanDataSource?.['Ad Scan'] || {};
  const users = adData?.['AD Users']?.[0] || [];

  return users.map((user, index) => ({
    no: index + 1,
    username: user.SamAccountName || user.UserName || '',
    display_name: user.DisplayName || user.Name || '',
    admin_user: user.AdminUser ? 'Yes' : 'No',
    ou: user.OU || user.OrganizationalUnit || ''
  }));
}

/**
 * AD Users - All Users Table
 */
export const ADAllUsersTableWidget = ({ data, config = {}, style = {}, pagination }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'Username': 'showUsername', 'Display Name': 'showDisplayName', 'Admin User': 'showAdminUser', 'OU': 'showOU' });
  const {
    title = 'All AD Users',
    showNo = true,
    showUsername = true,
    showDisplayName = true,
    showAdminUser = true,
    showOU = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'ad-all-users-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const _allUsers_raw = extractADAllUsersData(data);
  const allUsers = applyTableSort(
    applyTableFilters(_allUsers_raw, config.tableFilters || config?.tableFilters),
    config.tableSort
  );

  const displayUsers = pagination
    ? (() => {
      const slice = getPaginationSlice(pagination);
      return allUsers.slice(slice.startIndex, slice.endIndex);
    })()
    : allUsers;

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showNo, showUsername, showDisplayName, showAdminUser, showOU].filter(Boolean).length;
  const templateColumns = visibleColumns === 5 
    ? '0.5fr 1.5fr 2fr 1fr 2fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showUsername && <div style={headerStyle}>{getDisplayHeader(config, 'Username')}</div>}
            {showDisplayName && <div style={headerStyle}>{getDisplayHeader(config, 'Display Name')}</div>}
            {showAdminUser && <div style={headerStyle}>{getDisplayHeader(config, 'Admin User')}</div>}
            {showOU && <div style={headerStyle}>{getDisplayHeader(config, 'OU')}</div>}
          </div>
          <div className="open-ports-body">
            {displayUsers.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No users found.</p>
              </div>
            ) : (
              displayUsers.map((user, idx) => (
                <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                  {showNo && <div className="port-number" style={rowStyleMerged}>{user.no}</div>}
                  {showUsername && <div className="port-ip" style={rowStyleMerged}>{user.username}</div>}
                  {showDisplayName && <div className="port-service" style={rowStyleMerged}>{user.display_name}</div>}
                  {showAdminUser && <div className="port-service" style={rowStyleMerged}>{user.admin_user}</div>}
                  {showOU && <div className="port-service" style={rowStyleMerged}>{user.ou}</div>}
                </div>
              ))
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract inactive AD users data from scan results
 * Matches _Ad_inactive_Account_status from template2/AD.js:19-34
 */
export function extractInactiveADUsersData(data) {
  const usersData = [];

  if (!data) return usersData;

  // Extract from raw scan data
  const scanDataSource = data.scanData || data.data || data;
  const adData = scanDataSource?.['Ad Scan'] || {};
  const inactives = adData?.['inactive_accounts'] || [];

  if (!Array.isArray(inactives)) return usersData;

  let count = 1;
  for (const inactive of inactives) {
    usersData.push({
      no: count,
      username: inactive.username || '-',
      display_name: inactive.display_name || '-',
      admin_user: inactive?.is_admin ? 'Yes' : 'No',
      ou: inactive.ou || '-'
    });
    count++;
  }

  return usersData;
}

/**
 * AD Users - Inactive Users Table
 * Shows inactive Active Directory user accounts
 * Matches Ad_Account_status Inactive AD Users table from template2/AD.js:532-540
 * Widget auto-sizes to fit all content
 */
export const ADInactiveUsersTableWidget = ({ data, config = {}, style = {}, pagination }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'Username': 'showUsername', 'Display Name': 'showDisplayName', 'Admin User': 'showAdminUser', 'OU': 'showOU' });
  const {
    title = 'Inactive AD Users',
    showNo = true,
    showUsername = true,
    showDisplayName = true,
    showAdminUser = true,
    showOU = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'ad-inactive-users-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  // Extract all inactive users data
  const _allInactiveUsers_raw = extractInactiveADUsersData(data);
  const allInactiveUsers = applyTableSort(
    applyTableFilters(_allInactiveUsers_raw, config.tableFilters || config?.tableFilters),
    config.tableSort
  );

  // Apply pagination if provided
  const displayUsers = pagination
    ? (() => {
      const slice = getPaginationSlice(pagination);
      return allInactiveUsers.slice(slice.startIndex, slice.endIndex);
    })()
    : allInactiveUsers;

  // Get font size from style prop, default to 14px
  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  // Dynamic grid columns based on visible columns
  const visibleColumns = [showNo, showUsername, showDisplayName, showAdminUser, showOU].filter(Boolean).length;
  const templateColumns = visibleColumns === 5 
    ? '0.5fr 1.5fr 2fr 1fr 2fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}

      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showUsername && <div style={headerStyle}>{getDisplayHeader(config, 'Username')}</div>}
            {showDisplayName && <div style={headerStyle}>{getDisplayHeader(config, 'Display Name')}</div>}
            {showAdminUser && <div style={headerStyle}>{getDisplayHeader(config, 'Admin User')}</div>}
            {showOU && <div style={headerStyle}>{getDisplayHeader(config, 'OU')}</div>}
          </div>

          <div className="open-ports-body">
            {displayUsers.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No inactive users found.</p>
              </div>
            ) : (
              displayUsers.map((user, idx) => (
                <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                  {showNo && <div className="port-number" style={rowStyleMerged}>{user.no}</div>}
                  {showUsername && <div className="port-ip" style={rowStyleMerged}>{user.username}</div>}
                  {showDisplayName && <div className="port-service" style={rowStyleMerged}>{user.display_name}</div>}
                  {showAdminUser && <div className="port-service" style={rowStyleMerged}>{user.admin_user}</div>}
                  {showOU && <div className="port-service" style={rowStyleMerged}>{user.ou}</div>}
                </div>
              ))
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * AD Computers - All Computers Table
 * Shows all Active Directory computer accounts
 */
/**
 * AD Computers - All Computers Table
 * Shows all Active Directory computer accounts
 */
export const ADAllComputersTableWidget = ({ data, config = {}, style = {}, pagination }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'Name': 'showName', 'Host': 'showHost', 'OS': 'showOS', 'OU': 'showOU', 'Age': 'showAge' });
  const {
    title = 'All AD Computers',
    showNo = true,
    showName = true,
    showHost = true,
    showOS = true,
    showOU = true,
    showAge = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'ad-all-computers-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const dataExtractor = (inputData) => {
    // Handle wrapped data structure (scanData, data, or direct)
    const scanDataSource = inputData?.scanData || inputData?.data || inputData;
    const adData = scanDataSource?.['Ad Scan'] || {};
    const computers = adData?.Computers || [];

    return computers.map((computer, index) => {
      // Calculate Age
      let age = '-';
      const createdValue = computer.whenCreated || computer.created;
      if (createdValue) {
        try {
          // Parse string like "20230101000000.0Z" or standard ISO
          // AD generalized time format: YYYYMMDDHHMMSS.0Z
          let dateStr = createdValue;
          let createdDate;

          // Handle AD Generalized Time format (e.g., "20190410134327.0Z")
          if (typeof dateStr === 'string' && dateStr.includes('.0Z')) {
            const year = parseInt(dateStr.substring(0, 4));
            const month = parseInt(dateStr.substring(4, 6)) - 1; // Months are 0-indexed
            const day = parseInt(dateStr.substring(6, 8));
            const hour = parseInt(dateStr.substring(8, 10));
            const minute = parseInt(dateStr.substring(10, 12));
            const second = parseInt(dateStr.substring(12, 14));
            createdDate = new Date(Date.UTC(year, month, day, hour, minute, second));
          } else {
            // Try standard parsing
            createdDate = new Date(dateStr);
          }

          if (!isNaN(createdDate.getTime())) {
            const now = new Date();
            const diffTime = Math.abs(now - createdDate);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            
            if (diffDays > 365) {
                const years = Math.floor(diffDays / 365);
                const days = diffDays % 365;
               age = `${years} year${years > 1 ? 's' : ''} ${days} day${days !== 1 ? 's' : ''}`;
            } else {
               age = `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
            }
          }
        } catch (e) {
          console.warn('Error parsing date:', createdValue);
        }
      }

      return {
        no: index + 1,
        name: computer.Name || computer.ComputerName || computer.name || '',
        host: computer.DNSHostName || computer.Hostname || computer.host || '',
        os: computer.OperatingSystem || computer.OS || computer.os || '',
        ou: computer.OU || computer.OrganizationalUnit || computer.ou_or_container || '',
        age: age
      };
    });
  };

  // Extract all data
  const _allComputers_raw = dataExtractor(data);
  const allComputers = applyTableSort(
    applyTableFilters(_allComputers_raw, config.tableFilters || config?.tableFilters),
    config.tableSort
  );

  // Apply pagination if provided
  const displayComputers = pagination
    ? (() => {
        const slice = getPaginationSlice(pagination);
        return allComputers.slice(slice.startIndex, slice.endIndex);
      })()
    : allComputers;

  // Get font size from style prop, default to 14px
  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  // Dynamic grid columns based on visible columns
  const visibleColumns = [showNo, showName, showHost, showOS, showOU, showAge].filter(Boolean).length;
  const templateColumns = visibleColumns === 6
    ? '0.5fr 1.5fr 1.5fr 1.5fr 2fr 1fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}

      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showName && <div style={headerStyle}>{getDisplayHeader(config, 'Name')}</div>}
            {showHost && <div style={headerStyle}>{getDisplayHeader(config, 'Host')}</div>}
            {showOS && <div style={headerStyle}>{getDisplayHeader(config, 'OS')}</div>}
            {showOU && <div style={headerStyle}>{getDisplayHeader(config, 'OU')}</div>}
            {showAge && <div style={headerStyle}>{getDisplayHeader(config, 'Age')}</div>}
          </div>

          <div className="open-ports-body">
            {displayComputers.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No computers found.</p>
              </div>
            ) : (
              displayComputers.map((computer, idx) => (
                <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                  {showNo && <div className="port-number" style={rowStyleMerged}>{computer.no}</div>}
                  {showName && <div className="port-ip" style={rowStyleMerged}>{computer.name}</div>}
                  {showHost && <div className="port-service" style={rowStyleMerged}>{computer.host}</div>}
                  {showOS && <div className="port-service" style={rowStyleMerged}>{computer.os}</div>}
                  {showOU && <div className="port-service" style={rowStyleMerged}>{computer.ou}</div>}
                  {showAge && <div className="port-service" style={rowStyleMerged}>{computer.age}</div>}
                </div>
              ))
            )}
          </div>
        </div>
    </div>
  );
};


/**
 * AD Computers - Inactive Computers Table
 * Shows inactive Active Directory computer accounts
 */
/**
 * Extract inactive AD computers data from scan results
 * Matches _Ad_inactive_Computer_status from template2/AD.js:48-61
 */
export function extractInactiveADComputersData(data) {
  const computersData = [];

  if (!data) return computersData;

  // Extract from raw scan data
  const scanDataSource = data.scanData || data.data || data;
  const adData = scanDataSource?.['Ad Scan'] || {};
  const inactives = adData?.['inactive_computers'] || [];

  if (!Array.isArray(inactives)) return computersData;

  let count = 1;
  for (const inactive of inactives) {
    computersData.push({
      no: count,
      name: inactive.name || '-',
      host: inactive.host || '-',
      os: inactive.os || '-',
      ou: inactive.ou_or_container || '-'
    });
    count++;
  }

  return computersData;
}

/**
 * AD Computers - Inactive Computers Table
 * Shows inactive Active Directory computers
 * Matches Ad_Computer_status Inactive AD Computers table from template2/AD.js:389-401
 * Widget auto-sizes to fit all content
 */
export const ADInactiveComputersTableWidget = ({ data, config = {}, style = {}, pagination }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'Name': 'showName', 'Host': 'showHost', 'OS': 'showOS', 'OU': 'showOU' });
  const {
    title = 'Inactive AD Computers',
    showNo = true,
    showName = true,
    showHost = true,
    showOS = true,
    showOU = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'ad-inactive-computers-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  // Extract all inactive computers data
  const _allInactiveComputers_raw = extractInactiveADComputersData(data);
  const allInactiveComputers = applyTableSort(
    applyTableFilters(_allInactiveComputers_raw, config.tableFilters || config?.tableFilters),
    config.tableSort
  );

  // Apply pagination if provided
  const displayComputers = pagination
    ? (() => {
      const slice = getPaginationSlice(pagination);
      return allInactiveComputers.slice(slice.startIndex, slice.endIndex);
    })()
    : allInactiveComputers;

  // Get font size from style prop, default to 14px
  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  // Dynamic grid columns based on visible columns
  const visibleColumns = [showNo, showName, showHost, showOS, showOU].filter(Boolean).length;
  const templateColumns = visibleColumns === 5 
    ? '0.5fr 1.5fr 1.5fr 1.5fr 2fr' 
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}

      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showName && <div style={headerStyle}>{getDisplayHeader(config, 'Name')}</div>}
            {showHost && <div style={headerStyle}>{getDisplayHeader(config, 'Host')}</div>}
            {showOS && <div style={headerStyle}>{getDisplayHeader(config, 'OS')}</div>}
            {showOU && <div style={headerStyle}>{getDisplayHeader(config, 'OU')}</div>}
          </div>

          <div className="open-ports-body">
            {displayComputers.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No inactive computers found.</p>
              </div>
            ) : (
              displayComputers.map((computer, idx) => (
                <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                  {showNo && <div className="port-number" style={rowStyleMerged}>{computer.no}</div>}
                  {showName && <div className="port-ip" style={rowStyleMerged}>{computer.name}</div>}
                  {showHost && <div className="port-service" style={rowStyleMerged}>{computer.host}</div>}
                  {showOS && <div className="port-service" style={rowStyleMerged}>{computer.os}</div>}
                  {showOU && <div className="port-service" style={rowStyleMerged}>{computer.ou}</div>}
                </div>
              ))
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract AD Groups risk assessment data from scan results
 * Matches _Ad_assess_group_risks from template2/AD.js:73-132
 */
export function extractADGroupsRiskData(data) {
  const groupsData = [];

  if (!data) return groupsData;

  // Extract from raw scan data
  const scanDataSource = data.scanData || data.data || data;
  const adData = scanDataSource?.['Ad Scan'] || {};
  const groups = adData?.groups || [];

  if (!Array.isArray(groups)) return groupsData;

  // Match backend logic exactly: _Ad_assess_group_risks
  const privilegedGroups = [
    "Domain Admins",
    "Enterprise Admins",
    "Administrators",
    "Schema Admins",
    "Account Operators",
    "Backup Operators",
    "Remote Desktop Users",
    "Network Configuration Operators",
    "Power Users"
  ];

  for (const group of groups) {
    const {
      name,
      description,
      members = [],
      isSecurityGroup,
      isDomainLocal,
      managedBy,
      whenCreated
    } = group;

    // Handle different property name formats
    const groupName = name || group.Name || group.GroupName || '';
    const groupDescription = description || group.Description || '';
    const groupMembers = Array.isArray(members) ? members : (group.Members || []);
    const memberCount = Array.isArray(groupMembers) ? groupMembers.length : 0;
    const groupIsSecurityGroup = isSecurityGroup !== undefined ? isSecurityGroup : (group.IsSecurityGroup || group.GroupType === 2 || group.Type === 'Security');
    const groupIsDomainLocal = isDomainLocal !== undefined ? isDomainLocal : (group.IsDomainLocal || group.GroupScope === 2 || group.Scope === 'Domain Local');
    const groupManagedBy = managedBy || group.ManagedBy || null;
    const groupWhenCreated = whenCreated || group.WhenCreated || group.Created || '';

    const isPrivileged = privilegedGroups.includes(groupName);
    const hasManager = !!groupManagedBy;

    let risk = "Low";

    // Rule 1: Privileged group with members
    if (isPrivileged && memberCount > 0) {
      risk = "High";
    }
    // Rule 2: Privileged group with 0 members
    else if (isPrivileged && memberCount === 0) {
      risk = "Medium";
    }
    // Rule 3: No manager and is security group
    else if (!hasManager && groupIsSecurityGroup) {
      risk = "Medium";
    }
    // Rule 4: Domain local and is security group
    else if (groupIsDomainLocal && groupIsSecurityGroup) {
      risk = "Medium";
    }

    groupsData.push({
      name: groupName,
      description: groupDescription || "-",
      members: memberCount,
      scope: groupIsDomainLocal ? "Domain Local" : "Global/Universal",
      type: groupIsSecurityGroup ? "Security" : "Distribution",
      created: groupWhenCreated,
      risk
    });
  }

  return groupsData;
}

/**
 * AD Groups - Group Risk Assessment Table
 * Shows all AD groups with risk assessment
 * Matches Ad_Groups_status table from template2/AD.js:449-460
 * Headers: ['Name', 'Description', "Members", "Scope", "Type", "Created", "Risk"] (no "No." column)
 * Widget auto-sizes to fit all content
 */
export const ADGroupsRiskTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'Name': 'showName', 'Description': 'showDescription', 'Members': 'showMembers', 'Scope': 'showScope', 'Type': 'showType', 'Created': 'showCreated', 'Risk': 'showRisk' });
  const {
    title = 'Assess Group Risks',
    showName = true,
    showDescription = true,
    showMembers = true,
    showScope = true,
    showType = true,
    showCreated = true,
    showRisk = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'ad-groups-risk-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  // Extract all groups risk data
  const _allGroups_raw = extractADGroupsRiskData(data);
  const allGroups = applyTableSort(
    applyTableFilters(_allGroups_raw, config.tableFilters || config?.tableFilters),
    config.tableSort
  );

  // Apply pagination if provided
  const displayGroups = pagination
    ? (() => {
      const slice = getPaginationSlice(pagination);
      return allGroups.slice(slice.startIndex, slice.endIndex);
    })()
    : allGroups;

  // Get font size from style prop, default to 14px
  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  // Dynamic grid columns based on visible columns
  const visibleColumns = [showName, showDescription, showMembers, showScope, showType, showCreated, showRisk].filter(Boolean).length;
  const templateColumns = visibleColumns === 7 
    ? '1.5fr 2fr 0.8fr 1.2fr 1fr 1.2fr 0.8fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  const riskColors = {
    'High': theme?.statusColors?.red || '#d71920',
    'Medium': theme?.statusColors?.yellow || '#fdbc13',
    'Low': theme?.statusColors?.green || '#6fca42'
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}

      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showName && <div style={headerStyle}>{getDisplayHeader(config, 'Name')}</div>}
            {showDescription && <div style={headerStyle}>{getDisplayHeader(config, 'Description')}</div>}
            {showMembers && <div style={headerStyle}>{getDisplayHeader(config, 'Members')}</div>}
            {showScope && <div style={headerStyle}>{getDisplayHeader(config, 'Scope')}</div>}
            {showType && <div style={headerStyle}>{getDisplayHeader(config, 'Type')}</div>}
            {showCreated && <div style={headerStyle}>{getDisplayHeader(config, 'Created')}</div>}
            {showRisk && <div style={headerStyle}>{getDisplayHeader(config, 'Risk')}</div>}
          </div>

          <div className="open-ports-body">
            {displayGroups.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No groups found.</p>
              </div>
            ) : (
              displayGroups.map((group, idx) => {
                const riskColor = riskColors[group.risk] || '#666';
                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showName && <div className="port-ip" style={rowStyleMerged}>{group.name}</div>}
                    {showDescription && <div className="port-service" style={rowStyleMerged}>{group.description}</div>}
                    {showMembers && <div className="port-number" style={rowStyleMerged}>{group.members}</div>}
                    {showScope && <div className="port-service" style={rowStyleMerged}>{group.scope}</div>}
                    {showType && <div className="port-service" style={rowStyleMerged}>{group.type}</div>}
                    {showCreated && <div className="port-service" style={rowStyleMerged}>{group.created || '-'}</div>}
                    {showRisk && (
                      <div style={rowStyleMerged}>
                        <span style={{ color: riskColor, fontWeight: 'bold' }}>
                          {group.risk}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract AD OUs risk assessment data from scan results
 * Matches _Ad_assess_ou_risks from template2/AD.js:133-172
 */
export function extractADOUsRiskData(data) {
  const ousData = [];

  if (!data) return ousData;

  // Extract from raw scan data
  const scanDataSource = data.scanData || data.data || data;
  const adData = scanDataSource?.['Ad Scan'] || {};
  const ous = adData?.['OU_list'] || [];

  if (!Array.isArray(ous)) return ousData;

  // Match backend logic exactly: _Ad_assess_ou_risks
  for (const ou of ous) {
    const name = ou.ou || ou.OU || "Unnamed OU";
    const dn = ou.distinguishedName || ou.DistinguishedName || ou.DN || "-";
    const members = ou.members || ou.Members || [];
    const memberCount = Array.isArray(members) ? members.length : 0;

    let risk = "Low";
    const lowerName = name.toLowerCase();

    // Rule 1: Disabled OU has members
    if (lowerName.includes("disabled") && memberCount > 0) {
      risk = "High";
    }
    // Rule 2: Sensitive name (e.g., admin, backup, service)
    else if (/admin|backup|service/.test(lowerName)) {
      risk = "Medium";
    }
    // Rule 3: Empty OU
    else if (memberCount === 0) {
      risk = "Medium";
    }

    ousData.push({
      name,
      dn,
      members: memberCount,
      risk
    });
  }

  return ousData;
}

/**
 * AD OUs - Organizational Unit Risk Assessment Table
 * Shows all OUs with risk assessment
 * Matches Ad_Ou_status table from template2/AD.js:589-600
 * Headers: ['Name', 'DN', "Members", "Risk"] (no "No." column)
 * Widget auto-sizes to fit all content
 */
export const ADOUsRiskTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'Name': 'showName', 'DN': 'showDN', 'Members': 'showMembers', 'Risk': 'showRisk' });
  const {
    title = 'Assess OU Risks',
    showName = true,
    showDN = true,
    showMembers = true,
    showRisk = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'ad-ous-risk-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  // Extract all OUs risk data
  const _allOUs_raw = extractADOUsRiskData(data);
  const allOUs = applyTableSort(
    applyTableFilters(_allOUs_raw, config.tableFilters || config?.tableFilters),
    config.tableSort
  );

  // Apply pagination if provided
  const displayOUs = pagination
    ? (() => {
      const slice = getPaginationSlice(pagination);
      return allOUs.slice(slice.startIndex, slice.endIndex);
    })()
    : allOUs;

  // Get font size from style prop, default to 14px
  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  // Dynamic grid columns based on visible columns
  const visibleColumns = [showName, showDN, showMembers, showRisk].filter(Boolean).length;
  const templateColumns = visibleColumns === 4 
    ? '1.5fr 2fr 1fr 1fr' 
    : Array(visibleColumns).fill('1fr').join(' ');

  const riskColors = {
    'High': theme?.statusColors?.red || '#d71920',
    'Medium': theme?.statusColors?.yellow || '#fdbc13',
    'Low': theme?.statusColors?.green || '#6fca42'
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}

      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showName && <div style={headerStyle}>{getDisplayHeader(config, 'Name')}</div>}
            {showDN && <div style={headerStyle}>{getDisplayHeader(config, 'DN')}</div>}
            {showMembers && <div style={headerStyle}>{getDisplayHeader(config, 'Members')}</div>}
            {showRisk && <div style={headerStyle}>{getDisplayHeader(config, 'Risk')}</div>}
          </div>

          <div className="open-ports-body">
            {displayOUs.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No OUs found.</p>
              </div>
            ) : (
              displayOUs.map((ou, idx) => {
              const riskColor = riskColors[ou.risk] || '#666';
              const formattedDN = ou.dn && ou.dn !== '-'
                ? ou.dn.split(',').map((part, i, arr) => (
                  <React.Fragment key={i}>
                    {part.trim()}
                    {i < arr.length - 1 && <br />}
                  </React.Fragment>
                ))
                : '-';

              return (
                <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                  {showName && <div className="port-ip" style={rowStyleMerged}>{ou.name}</div>}
                  {showDN && <div className="port-service" style={rowStyleMerged}>{formattedDN}</div>}
                  {showMembers && <div className="port-number" style={rowStyleMerged}>{ou.members}</div>}
                  {showRisk && (
                    <div style={rowStyleMerged}>
                      <span style={{ color: riskColor, fontWeight: 'bold' }}>
                        {ou.risk}
                      </span>
                    </div>
                  )}
                </div>
              );
            })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract AD FSMO and Server Roles data from scan results
 * Matches _Ad_assess_fsmo_and_server_roles from template2/AD.js:173-263
 */
export function extractADFSMORolesData(data) {
  const report = [];

  if (!data) return report;

  // Extract from raw scan data
  // Backend: Ad_FSMO_status receives jsonData (which is the AD data object)
  // Backend: _Ad_assess_fsmo_and_server_roles(AD) accesses AD.fsmo_roles directly
  // So in frontend: data.scanData['Ad Scan'] is the AD object
  const scanDataSource = data.scanData || data.data || data;
  const adData = scanDataSource?.['Ad Scan'] || scanDataSource || {};

  // Backend: const fsmo = AD.fsmo_roles || {};
  // Try multiple possible key formats: fsmo_roles, FSMO_roles, fsmoRoles, etc.
  const fsmo = adData?.fsmo_roles ||
    adData?.FSMO_roles ||
    adData?.fsmoRoles ||
    adData?.FSMORoles ||
    adData?.fsmo ||
    {};

  // Backend: const serverRoles = fsmo["Server Roles"] || [];
  const serverRoles = fsmo["Server Roles"] ||
    fsmo["server_roles"] ||
    fsmo["ServerRoles"] ||
    fsmo["serverRoles"] ||
    [];

  // Enhanced debug logging to understand data structure
  const hasFsmo = fsmo && typeof fsmo === 'object' && Object.keys(fsmo).length > 0;
  console.log('[extractADFSMORolesData] Data extraction:', {
    hasData: !!data,
    hasScanData: !!scanDataSource,
    hasAdScan: !!adData,
    adDataKeys: adData ? Object.keys(adData).slice(0, 30) : [],
    // Check for fsmo in different locations and formats
    hasFsmoRoles_lower: !!adData?.fsmo_roles,
    hasFSMO_roles_upper: !!adData?.FSMO_roles,
    hasFsmoRoles_camel: !!adData?.fsmoRoles,
    hasFsmoRoles_FSMORoles: !!adData?.FSMORoles,
    hasFsmo: hasFsmo,
    fsmoKeys: hasFsmo ? Object.keys(fsmo).slice(0, 15) : [],
    serverRolesCount: Array.isArray(serverRoles) ? serverRoles.length : 0,
    serverRolesSample: Array.isArray(serverRoles) && serverRoles.length > 0 ? serverRoles.slice(0, 3) : []
  });

  // FSMO roles to check
  const fsmoRolesList = [
    "Schema Master",
    "Domain Naming Master",
    "RID Master",
    "Infrastructure Master",
    "PDC Emulator"
  ];

  // Helper function to get role value with multiple key format attempts
  const getRoleValue = (role) => {
    return fsmo[role] ||
      fsmo[role.replace(/\s+/g, '')] || // "Schema Master" -> "SchemaMaster"
      fsmo[role.toLowerCase().replace(/\s+/g, '_')] || // "Schema Master" -> "schema_master"
      fsmo[role.replace(/\s+/g, '') + 'DN'] || // "SchemaMasterDN"
      null;
  };

  // Check FSMO roles
  // Determine if all FSMO roles are on the same DC
  const holders = fsmoRolesList
    .filter(role => {
      const roleValue = getRoleValue(role);
      if (!roleValue) return false;
      const dn = typeof roleValue === 'string' ? roleValue : (roleValue.dn || roleValue.DN || roleValue.distinguishedName || '');
      return dn && typeof dn === 'string';
    })
    .map(role => {
      const roleValue = getRoleValue(role);
      const dn = typeof roleValue === 'string' ? roleValue : (roleValue.dn || roleValue.DN || roleValue.distinguishedName || '');
      // Backend: fsmo[role].split(",")[1] || ""
      // Extract DC name from DN (usually 2nd part after split by comma)
      if (!dn || typeof dn !== 'string') return "";
      const parts = dn.split(",");
      return parts.length > 1 ? parts[1].trim() : "";
    })
    .filter(x => x); // Remove empty

  const uniqueHolders = [...new Set(holders)];
  const singleHolder = uniqueHolders.length === 1 ? uniqueHolders[0] : null;

  for (const role of fsmoRolesList) {
    const roleValue = getRoleValue(role);
    if (!roleValue) continue;

    const dn = typeof roleValue === 'string' ? roleValue : (roleValue.dn || roleValue.DN || roleValue.distinguishedName || '');
    if (!dn || typeof dn !== 'string') continue;

    // Backend: dn.match(/CN=([^,]+)/g) - Array of CN parts
    // Then: dcNameMatch[1].replace("CN=", "") - Second CN part without "CN="
    const dcNameMatch = dn.match(/CN=([^,]+)/g); // Array of CN parts
    const dcName = dcNameMatch && dcNameMatch.length > 1
      ? dcNameMatch[1].replace("CN=", "")
      : "Unknown";

    let risk = "Low";
    let notes = "";
    if (singleHolder) {
      risk = "High";
      notes = `All FSMO roles are on a single Domain Controller (${singleHolder}) — single point of failure.`;
    }

    report.push({
      role,
      dcName,
      risk,
      note: notes
    });
  }

  // Assess Server Roles
  if (Array.isArray(serverRoles)) {
    for (const roleStr of serverRoles) {
      if (!roleStr || typeof roleStr !== 'string') continue;

      // roleStr format: "Role Name (ID: number)"
      const nameMatch = roleStr.match(/^(.+?)\s+\(ID:\s*(\d+)\)$/);
      if (!nameMatch) continue;

      const roleName = nameMatch[1];
      // const roleId = parseInt(nameMatch[2], 10);

      // Assign risk based on known risky roles on a DC
      let risk = "Low";
      let note = "Expected or low risk role";
      const highRiskRoles = ["DNS Server"];
      const mediumRiskRoles = [
        "File Server",
        "File and iSCSI Services",
        "File and Storage Services",
        "Remote Server Administration Tools",
        "Windows PowerShell ISE",
        "Windows PowerShell",
        "BitLocker Drive Encryption"
      ];

      if (highRiskRoles.includes(roleName)) {
        risk = "High";
        note = "Critical for AD operations; must be hardened and monitored.";
      } else if (mediumRiskRoles.includes(roleName)) {
        risk = "Medium";
        note = "Should be reviewed; potential attack surface or performance risk on DC.";
      }

      report.push({
        role: roleName,
        dcName: "-",
        risk,
        note
      });
    }
  }

  return report;
}

/**
 * AD FSMO Roles - Server Role Analysis Table
 * Shows FSMO roles and DC roles
 * Matches Ad_FSMO_status table from template2/AD.js:650-662
 * Headers: ['Role', 'DC Name', "Risk", "Note"] (no "No." column)
 * Widget auto-sizes to fit all content
 */
export const ADFSMORolesTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'Role': 'showRole', 'DC Name': 'showDCName', 'Risk': 'showRisk', 'Note': 'showNote' });
  const {
    title = 'FSMO and Server Role Analysis',
    showRole = true,
    showDCName = true,
    showRisk = true,
    showNote = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'ad-fsmo-roles-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  // Extract all FSMO roles data
  const _allRoles_raw = extractADFSMORolesData(data);
  const allRoles = applyTableSort(
    applyTableFilters(_allRoles_raw, config.tableFilters || config?.tableFilters),
    config.tableSort
  );

  // Apply pagination if provided
  const displayRoles = pagination
    ? (() => {
      const slice = getPaginationSlice(pagination);
      return allRoles.slice(slice.startIndex, slice.endIndex);
    })()
    : allRoles;

  // Get font size from style prop, default to 14px
  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  // Dynamic grid columns based on visible columns
  const visibleColumns = [showRole, showDCName, showRisk, showNote].filter(Boolean).length;
  const templateColumns = visibleColumns === 4 
    ? '1.5fr 1.5fr 0.8fr 2fr' 
    : Array(visibleColumns).fill('1fr').join(' ');

  const riskColors = {
    'High': theme?.statusColors?.red || '#d71920',
    'Medium': theme?.statusColors?.yellow || '#fdbc13',
    'Low': theme?.statusColors?.green || '#6fca42'
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}

      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showRole && <div style={headerStyle}>{getDisplayHeader(config, 'Role')}</div>}
            {showDCName && <div style={headerStyle}>{getDisplayHeader(config, 'DC Name')}</div>}
            {showRisk && <div style={headerStyle}>{getDisplayHeader(config, 'Risk')}</div>}
            {showNote && <div style={headerStyle}>{getDisplayHeader(config, 'Note')}</div>}
          </div>

          <div className="open-ports-body">
            {displayRoles.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No FSMO roles or server roles found.</p>
              </div>
            ) : (
              displayRoles.map((role, idx) => {
              const riskColor = riskColors[role.risk] || '#666';
              return (
                <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                  {showRole && <div className="port-ip" style={rowStyleMerged}>{role.role}</div>}
                  {showDCName && <div className="port-service" style={rowStyleMerged}>{role.dcName}</div>}
                  {showRisk && (
                    <div style={rowStyleMerged}>
                      <span style={{ color: riskColor, fontWeight: 'bold' }}>
                        {role.risk}
                      </span>
                    </div>
                  )}
                  {showNote && <div className="port-service" style={rowStyleMerged}>{role.note || '-'}</div>}
                </div>
              );
            })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract AD GPO data from scan results
 * Matches _Ad_assess_gpo_risks from template2/AD.js:264-336
 */
export function extractADGPORisksData(data) {
  const rows = [];

  if (!data) return rows;

  // Extract from raw scan data
  // Backend: Ad_GPO_status receives jsonData (which is the AD data object)
  // Backend: _Ad_assess_gpo_risks(AD) accesses AD["GPO Detail"] directly
  const scanDataSource = data.scanData || data.data || data;
  const adData = scanDataSource?.['Ad Scan'] || scanDataSource || {};

  // Backend: const gpos = AD["GPO Detail"] || {};
  const gpos = adData?.["GPO Detail"] || adData?.["GPO_Detail"] || adData?.["gpo_detail"] || adData?.["gpoDetail"] || {};

  if (!gpos || typeof gpos !== 'object' || Array.isArray(gpos)) {
    return rows;
  }

  const now = new Date();

  // Backend: for (const [gpoName, gpo] of Object.entries(gpos))
  for (const [gpoName, gpo] of Object.entries(gpos)) {
    if (!gpo || typeof gpo !== 'object') continue;

    const createdOn = gpo.CreatedOn || gpo.createdOn || gpo.Created || gpo.created;
    const lastModified = gpo.LastModified || gpo.lastModified || gpo.Modified || gpo.modified;
    const links = gpo.Links || gpo.links || [];
    const policies = gpo.Policies || gpo.policies || [];

    // Parse dates
    const createdDate = createdOn ? new Date(createdOn) : new Date();
    const lastModifiedDate = lastModified ? new Date(lastModified) : new Date();

    // Calculate how many days since last modification
    const daysSinceMod = Math.floor((now - lastModifiedDate) / (1000 * 60 * 60 * 24));

    // Risk flags - match backend logic exactly
    let risk = "Low";
    let notes = [];

    // 1. Check if linked anywhere
    if (!Array.isArray(links) || links.length === 0) {
      risk = "Medium";
      notes.push("GPO not linked to any scope");
    } else {
      // 2. Check if any link is disabled
      const disabledLinks = links.filter(l => {
        const enabled = l.Enabled !== undefined ? l.Enabled : (l.enabled !== undefined ? l.enabled : true);
        return !enabled;
      });
      if (disabledLinks.length > 0) {
        risk = risk === "Low" ? "Medium" : risk;
        notes.push("One or more GPO links are disabled");
      }
      // 3. Check for NoOverride links
      const noOverrideLinks = links.filter(l => {
        const noOverride = l.NoOverride !== undefined ? l.NoOverride : (l.noOverride !== undefined ? l.noOverride : false);
        return noOverride;
      });
      if (noOverrideLinks.length > 0) {
        risk = "Medium-High";
        notes.push("GPO has NoOverride set on one or more links");
      }
    }

    // 4. Check if last modified more than 365 days ago
    if (daysSinceMod > 365 && risk === "Low") {
      risk = "Low";
      notes.push(`Last modified ${daysSinceMod} days ago`);
    }

    // 5. Check for risky policy types (like printer deployments)
    const hasPrinterDeploy = Array.isArray(policies) && policies.some(p => {
      const policyType = p.Type || p.type || '';
      return policyType === "Printer Deployment" || policyType === "printer deployment";
    });
    if (hasPrinterDeploy && risk !== "Medium-High") {
      risk = "Medium";
      notes.push("Contains printer deployment policies");
    }

    // 6. Check if this is a default GPO (optional)
    const defaultGPOs = ["Default Domain Policy", "Default Domain Controllers Policy"];
    if (defaultGPOs.includes(gpoName)) {
      notes.push("Default system GPO");
    }

    // Format dates as YYYY-MM-DD
    const createdDateStr = createdDate.toISOString().split("T")[0];
    const lastModifiedStr = lastModifiedDate.toISOString().split("T")[0];

    rows.push({
      gpoName,
      createdDate: createdDateStr,
      lastModified: lastModifiedStr,
      linkedScopes: Array.isArray(links) ? links.length : 0,
      policies: Array.isArray(policies) ? policies.length : 0,
      risk,
      note: notes.join("; ") || "-"
    });
  }

  return rows;
}

/**
 * AD GPO - Group Policy Analysis Table
 * Shows GPO analysis with risk assessment
 * Matches Ad_GPO_status table from template2/AD.js:713-725
 * Headers: ['GPO Name', 'Created Date', "Last Modified", "Linked Scopes", "Policies", "Risk", "Note"] (no "No." column)
 * Widget auto-sizes to fit all content
 */
export const ADGPOAnalysisTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'GPO Name': 'showGPOName', 'Created Date': 'showCreatedDate', 'Last Modified': 'showLastModified', 'Linked Scopes': 'showLinkedScopes', 'Policies': 'showPolicies', 'Risk': 'showRisk', 'Note': 'showNote' });
  const {
    title = 'GPO Analysis',
    showGPOName = true,
    showCreatedDate = true,
    showLastModified = true,
    showLinkedScopes = true,
    showPolicies = true,
    showRisk = true,
    showNote = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'ad-gpo-analysis-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  // Extract all GPO data
  const _allGPOs_raw = extractADGPORisksData(data);
  const allGPOs = applyTableSort(
    applyTableFilters(_allGPOs_raw, config.tableFilters || config?.tableFilters),
    config.tableSort
  );

  // Apply pagination if provided
  const displayGPOs = pagination
    ? (() => {
      const slice = getPaginationSlice(pagination);
      return allGPOs.slice(slice.startIndex, slice.endIndex);
    })()
    : allGPOs;

  // Get font size from style prop, default to 14px
  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  // Dynamic grid columns based on visible columns
  const visibleColumns = [showGPOName, showCreatedDate, showLastModified, showLinkedScopes, showPolicies, showRisk, showNote].filter(Boolean).length;
  const templateColumns = visibleColumns === 7 
    ? '2fr 1fr 1fr 0.8fr 0.8fr 0.8fr 2fr' 
    : Array(visibleColumns).fill('1fr').join(' ');

  const riskColors = {
    'High': theme?.statusColors?.red || '#d71920',
    'Medium-High': '#fd8c19',
    'Medium': theme?.statusColors?.yellow || '#fdbc13',
    'Low': theme?.statusColors?.green || '#6fca42'
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}

      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showGPOName && <div style={headerStyle}>{getDisplayHeader(config, 'GPO Name')}</div>}
            {showCreatedDate && <div style={headerStyle}>{getDisplayHeader(config, 'Created Date')}</div>}
            {showLastModified && <div style={headerStyle}>{getDisplayHeader(config, 'Last Modified')}</div>}
            {showLinkedScopes && <div style={headerStyle}>{getDisplayHeader(config, 'Linked Scopes')}</div>}
            {showPolicies && <div style={headerStyle}>{getDisplayHeader(config, 'Policies')}</div>}
            {showRisk && <div style={headerStyle}>{getDisplayHeader(config, 'Risk')}</div>}
            {showNote && <div style={headerStyle}>{getDisplayHeader(config, 'Note')}</div>}
          </div>

          <div className="open-ports-body">
            {displayGPOs.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No GPO data found.</p>
              </div>
            ) : (
              displayGPOs.map((gpo, idx) => {
                const riskColor = riskColors[gpo.risk] || '#666';
                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showGPOName && <div className="port-ip" style={rowStyleMerged}>{gpo.gpoName}</div>}
                    {showCreatedDate && <div className="port-service" style={rowStyleMerged}>{gpo.createdDate}</div>}
                    {showLastModified && <div className="port-service" style={rowStyleMerged}>{gpo.lastModified}</div>}
                    {showLinkedScopes && <div className="port-service" style={rowStyleMerged}>{gpo.linkedScopes}</div>}
                    {showPolicies && <div className="port-service" style={rowStyleMerged}>{gpo.policies}</div>}
                    {showRisk && (
                      <div style={rowStyleMerged}>
                        <span style={{ color: riskColor, fontWeight: 'bold' }}>
                          {gpo.risk}
                        </span>
                      </div>
                    )}
                    {showNote && <div className="port-service" style={rowStyleMerged}>{gpo.note}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

// =============================================================================
// NETWORK SCAN PROOF TABLES
// =============================================================================

/**
 * Network - All Users Password Status Table
 * Shows all user accounts across network devices
 */
/**
 * Extract Network All Users Password Status data
 */
export function extractNetworkAllUsersPasswordData(data) {
  const scanDataSource = data?.scanData || data?.data || data;
  const networkDevices = scanDataSource?.network_devices || [];
  const rows = [];

  networkDevices.forEach(device => {
    const system = device.system || {};
    const users = system.users || [];

    users.forEach(user => {
      rows.push({
        username: user.Name || user.Username || '',
        computer: device.ip || device.hostname || '',
        account_type: user.AccountType || user.Type || 'Standard',
        password_required: user.PasswordRequired ? 'Yes' : 'No',
        password_expires: user.PasswordExpires ? 'Yes' : 'No'
      });
    });
  });

  return rows;
}

/**
 * Network - All Users Password Status Table
 */
export const NetworkAllUsersPasswordTableWidget = ({ data, config = {}, style = {}, pagination }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'Username': 'showUsername', 'Computer': 'showComputer', 'Account Type': 'showAccountType', 'Password Required': 'showPasswordRequired', 'Password Expires': 'showPasswordExpires' });
  const {
    title = 'All Users Password Status',
    showUsername = true,
    showComputer = true,
    showAccountType = true,
    showPasswordRequired = true,
    showPasswordExpires = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'network-all-users-password-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const _allUsers_raw = extractNetworkAllUsersPasswordData(data);
  const allUsers = applyTableSort(
    applyTableFilters(_allUsers_raw, config.tableFilters || config?.tableFilters),
    config.tableSort
  );

  const displayUsers = pagination
    ? (() => {
      const slice = getPaginationSlice(pagination);
      return allUsers.slice(slice.startIndex, slice.endIndex);
    })()
    : allUsers;

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showUsername, showComputer, showAccountType, showPasswordRequired, showPasswordExpires].filter(Boolean).length;
  const templateColumns = Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showUsername && <div style={headerStyle}>{getDisplayHeader(config, 'Username')}</div>}
            {showComputer && <div style={headerStyle}>{getDisplayHeader(config, 'Computer')}</div>}
            {showAccountType && <div style={headerStyle}>{getDisplayHeader(config, 'Account Type')}</div>}
            {showPasswordRequired && <div style={headerStyle}>{getDisplayHeader(config, 'Password Required')}</div>}
            {showPasswordExpires && <div style={headerStyle}>{getDisplayHeader(config, 'Password Expires')}</div>}
          </div>
          <div className="open-ports-body">
            {displayUsers.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No user data found.</p>
              </div>
            ) : (
              displayUsers.map((user, idx) => (
                <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                  {showUsername && <div className="port-ip" style={rowStyleMerged}>{user.username}</div>}
                  {showComputer && <div className="port-ip" style={rowStyleMerged}>{user.computer}</div>}
                  {showAccountType && <div className="port-service" style={rowStyleMerged}>{user.account_type}</div>}
                  {showPasswordRequired && <div className="port-service" style={rowStyleMerged}>{user.password_required}</div>}
                  {showPasswordExpires && <div className="port-service" style={rowStyleMerged}>{user.password_expires}</div>}
                </div>
              ))
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Network - Users With Non-Expiring Passwords Table
 * Shows users with passwords that don't expire
 */
/**
 * Extract Network Users With Non-Expiring Passwords data
 */
export function extractNetworkNonExpiringPasswordsData(data) {
  const scanDataSource = data?.scanData || data?.data || data;
  const networkDevices = scanDataSource?.network_devices || [];
  const rows = [];

  networkDevices.forEach(device => {
    const system = device.system || {};
    const users = system.users || [];

    users.forEach(user => {
      if (!user.PasswordExpires) {
        rows.push({
          computer_ip: device.ip || device.hostname || '',
          username: user.Name || user.Username || '',
          account_type: user.AccountType || user.Type || 'Standard',
          password_required: user.PasswordRequired ? 'Yes' : 'No'
        });
      }
    });
  });

  return rows;
}

/**
 * Network - Users With Non-Expiring Passwords Table
 */
export const NetworkNonExpiringPasswordsTableWidget = ({ data, config = {}, style = {}, pagination }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'Computer IP': 'showComputerIP', 'Username': 'showUsername', 'Account Type': 'showAccountType', 'Password Required': 'showPasswordRequired' });
  const {
    title = 'Users With Non-Expiring Passwords',
    showComputerIP = true,
    showUsername = true,
    showAccountType = true,
    showPasswordRequired = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'network-non-expiring-passwords-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const _allUsers_raw = extractNetworkNonExpiringPasswordsData(data);
  const allUsers = applyTableSort(
    applyTableFilters(_allUsers_raw, config.tableFilters || config?.tableFilters),
    config.tableSort
  );

  const displayUsers = pagination
    ? (() => {
      const slice = getPaginationSlice(pagination);
      return allUsers.slice(slice.startIndex, slice.endIndex);
    })()
    : allUsers;

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showComputerIP, showUsername, showAccountType, showPasswordRequired].filter(Boolean).length;
  const templateColumns = Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showComputerIP && <div style={headerStyle}>{getDisplayHeader(config, 'Computer IP')}</div>}
            {showUsername && <div style={headerStyle}>{getDisplayHeader(config, 'Username')}</div>}
            {showAccountType && <div style={headerStyle}>{getDisplayHeader(config, 'Account Type')}</div>}
            {showPasswordRequired && <div style={headerStyle}>{getDisplayHeader(config, 'Password Required')}</div>}
          </div>
          <div className="open-ports-body">
            {displayUsers.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No user data found.</p>
              </div>
            ) : (
              displayUsers.map((user, idx) => (
                <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                  {showComputerIP && <div className="port-ip" style={rowStyleMerged}>{user.computer_ip}</div>}
                  {showUsername && <div className="port-ip" style={rowStyleMerged}>{user.username}</div>}
                  {showAccountType && <div className="port-service" style={rowStyleMerged}>{user.account_type}</div>}
                  {showPasswordRequired && <div className="port-service" style={rowStyleMerged}>{user.password_required}</div>}
                </div>
              ))
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Network - Admin Accounts Used for Daily Work Table
 * Shows admin accounts that might be used for regular work
 */
/**
 * Extract Network Admin Accounts Used for Daily Work data
 */
export function extractNetworkAdminDailyWorkData(data) {
  const scanDataSource = data?.scanData || data?.data || data;
  const networkDevices = scanDataSource?.network_devices || [];
  const rows = [];

  networkDevices.forEach(device => {
    const system = device.system || {};
    const users = system.users || [];

    users.forEach(user => {
      const isAdmin = user.AccountType === 'Administrator' ||
        user.Name?.toLowerCase().includes('admin') ||
        (user.Groups && user.Groups.some(g => g.toLowerCase().includes('admin')));

      if (isAdmin) {
        rows.push({
          computer_ip: device.ip || device.hostname || '',
          group: user.Groups ? (Array.isArray(user.Groups) ? user.Groups.join(', ') : user.Groups) : 'Administrators',
          account: user.Name || user.Username || ''
        });
      }
    });
  });

  return rows;
}

/**
 * Network - Admin Accounts Used for Daily Work Table
 */
export const NetworkAdminDailyWorkTableWidget = ({ data, config = {}, style = {}, pagination }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'Computer IP': 'showComputerIP', 'Group': 'showGroup', 'Account': 'showAccount' });
  const {
    title = 'Admin Accounts Used for Daily Work',
    showComputerIP = true,
    showGroup = true,
    showAccount = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'network-admin-daily-work-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const _allUsers_raw = extractNetworkAdminDailyWorkData(data);
  const allUsers = applyTableSort(
    applyTableFilters(_allUsers_raw, config.tableFilters || config?.tableFilters),
    config.tableSort
  );

  const displayUsers = pagination
    ? (() => {
      const slice = getPaginationSlice(pagination);
      return allUsers.slice(slice.startIndex, slice.endIndex);
    })()
    : allUsers;

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showComputerIP, showGroup, showAccount].filter(Boolean).length;
  const templateColumns = Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showComputerIP && <div style={headerStyle}>{getDisplayHeader(config, 'Computer IP')}</div>}
            {showGroup && <div style={headerStyle}>{getDisplayHeader(config, 'Group')}</div>}
            {showAccount && <div style={headerStyle}>{getDisplayHeader(config, 'Account')}</div>}
          </div>
          <div className="open-ports-body">
            {displayUsers.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No user data found.</p>
              </div>
            ) : (
              displayUsers.map((user, idx) => (
                <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                  {showComputerIP && <div className="port-ip" style={rowStyleMerged}>{user.computer_ip}</div>}
                  {showGroup && <div className="port-service" style={rowStyleMerged}>{user.group}</div>}
                  {showAccount && <div className="port-ip" style={rowStyleMerged}>{user.account}</div>}
                </div>
              ))
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Network - Disk Usage by Device Table
 * Shows disk usage for all network devices
 */
/**
 * Extract Network Disk Usage data
 */
export function extractNetworkDiskUsageData(data) {
  const scanDataSource = data?.scanData || data?.data || data;
  const networkDevices = scanDataSource?.network_devices || [];
  const rows = [];

  networkDevices.forEach(device => {
    const system = device.system || {};
    const disks = system.disks || [];

    disks.forEach(disk => {
      const usedSpace = disk.UsedSpace || disk.used || 0;
      const totalSpace = disk.TotalSpace || disk.total || 1;
      const usagePercent = Math.round((usedSpace / totalSpace) * 100);

      let status = 'Good';
      if (usagePercent >= 95) status = 'Critical';
      else if (usagePercent >= 85) status = 'Warning';

      rows.push({
        device: device.ip || device.hostname || '',
        drive_letter: disk.DriveLetter || disk.drive || '',
        disk_usage: `${usagePercent}%`,
        used_gb: Math.round(usedSpace / (1024 ** 3)),
        total_gb: Math.round(totalSpace / (1024 ** 3)),
        status
      });
    });
  });

  return rows;
}

/**
 * Network - Disk Usage by Device Table
 */
export const NetworkDiskUsageTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'Device': 'showDevice', 'Drive Letter': 'showDriveLetter', 'Disk Usage': 'showDiskUsage', 'Used (GB)': 'showUsedGB', 'Total (GB)': 'showTotalGB', 'Status': 'showStatus' });
  const {
    title = 'Disk Usage by Device',
    showDevice = true,
    showDriveLetter = true,
    showDiskUsage = true,
    showUsedGB = true,
    showTotalGB = true,
    showStatus = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'network-disk-usage-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const _allUsage_raw = extractNetworkDiskUsageData(data);
  const allUsage = applyTableSort(
    applyTableFilters(_allUsage_raw, config.tableFilters || config?.tableFilters),
    config.tableSort
  );

  const displayUsage = pagination
    ? (() => {
      const slice = getPaginationSlice(pagination);
      return allUsage.slice(slice.startIndex, slice.endIndex);
    })()
    : allUsage;

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showDevice, showDriveLetter, showDiskUsage, showUsedGB, showTotalGB, showStatus].filter(Boolean).length;
  const templateColumns = Array(visibleColumns).fill('1fr').join(' ');

  const statusColors = {
    'Critical': theme?.statusColors?.red || '#d71920',
    'Warning': theme?.statusColors?.yellow || '#fdbc13',
    'Good': theme?.statusColors?.green || '#6fca42'
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showDevice && <div style={headerStyle}>{getDisplayHeader(config, 'Device')}</div>}
            {showDriveLetter && <div style={headerStyle}>{getDisplayHeader(config, 'Drive Letter')}</div>}
            {showDiskUsage && <div style={headerStyle}>{getDisplayHeader(config, 'Disk Usage')}</div>}
            {showUsedGB && <div style={headerStyle}>{getDisplayHeader(config, 'Used (GB)')}</div>}
            {showTotalGB && <div style={headerStyle}>{getDisplayHeader(config, 'Total (GB)')}</div>}
            {showStatus && <div style={headerStyle}>{getDisplayHeader(config, 'Status')}</div>}
          </div>
          <div className="open-ports-body">
            {displayUsage.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No disk usage data found.</p>
              </div>
            ) : (
              displayUsage.map((disk, idx) => (
                <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                  {showDevice && <div className="port-ip" style={rowStyleMerged}>{disk.device}</div>}
                  {showDriveLetter && <div className="port-number" style={rowStyleMerged}>{disk.drive_letter}</div>}
                  {showDiskUsage && <div className="port-service" style={rowStyleMerged}>{disk.disk_usage}</div>}
                  {showUsedGB && <div className="port-number" style={rowStyleMerged}>{disk.used_gb}</div>}
                  {showTotalGB && <div className="port-number" style={rowStyleMerged}>{disk.total_gb}</div>}
                  {showStatus && (
                    <div style={rowStyleMerged}>
                      <span style={{ color: statusColors[disk.status] || '#666', fontWeight: 'bold' }}>
                        {disk.status}
                      </span>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Network - Backup Status by Device Table
 * Shows backup status for all network devices
 * Widget auto-sizes to fit all content
 */
export const NetworkBackupStatusTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'Device': 'showDevice', 'Hostname': 'showHostname', 'Last Backup': 'showLastBackup', 'Status': 'showStatus', 'Notes': 'showNotes' });
  const {
    title = 'Backup Status by Device',
    showDevice = true,
    showHostname = true,
    showLastBackup = true,
    showStatus = true,
    showNotes = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'network-backup-status-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  // Extract all backup data
  const _allBackupData_raw = extractBackupStatusData(data);
  const allBackupData = applyTableSort(
    applyTableFilters(_allBackupData_raw, config.tableFilters || config?.tableFilters),
    config.tableSort
  );

  // Apply pagination if provided
  const displayBackups = pagination
    ? (() => {
      const slice = getPaginationSlice(pagination);
      return allBackupData.slice(slice.startIndex, slice.endIndex);
    })()
    : allBackupData;

  // Get font size from style prop, default to 14px
  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  // Dynamic grid columns based on visible columns
  const visibleColumns = [showDevice, showHostname, showLastBackup, showStatus, showNotes].filter(Boolean).length;
  const templateColumns = visibleColumns === 5 
    ? '1fr 1.5fr 1fr 1fr 2fr' 
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}

      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showDevice && <div style={headerStyle}>{getDisplayHeader(config, 'Device')}</div>}
            {showHostname && <div style={headerStyle}>{getDisplayHeader(config, 'Hostname')}</div>}
            {showLastBackup && <div style={headerStyle}>{getDisplayHeader(config, 'Last Backup')}</div>}
            {showStatus && <div style={headerStyle}>{getDisplayHeader(config, 'Status')}</div>}
            {showNotes && <div style={headerStyle}>{getDisplayHeader(config, 'Notes')}</div>}
          </div>

          <div className="open-ports-body">
            {displayBackups.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No devices with backup configuration found.</p>
              </div>
            ) : (
              displayBackups.map((backup, idx) => {
                // Determine status color based on backup status and notes
                let statusColor = '#666';
                let displayStatus = backup.status;

                if (backup.notes && backup.notes.includes("Backup completed.")) {
                  statusColor = theme?.statusColors?.green || '#6fca42'; // Green for completed
                  if (displayStatus === '-') displayStatus = 'Good';
                } else if (backup.status && backup.status !== '-') {
                  // Use status from data if available
                  const statusLower = backup.status.toLowerCase();
                  if (statusLower.includes('good') || statusLower.includes('completed') || statusLower.includes('success')) {
                    statusColor = theme?.statusColors?.green || '#6fca42';
                  } else if (statusLower.includes('warning') || statusLower.includes('pending')) {
                    statusColor = theme?.statusColors?.yellow || '#fdbc13';
                  } else if (statusLower.includes('critical') || statusLower.includes('failed') || statusLower.includes('error')) {
                    statusColor = theme?.statusColors?.red || '#d71920';
                  }
                }

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showDevice && <div className="port-ip" style={rowStyleMerged}>{backup.device}</div>}
                    {showHostname && <div className="port-ip" style={rowStyleMerged}>{backup.hostname}</div>}
                    {showLastBackup && <div className="port-number" style={rowStyleMerged}>{backup.last_backup}</div>}
                    {showStatus && (
                      <div style={rowStyleMerged}>
                        <span style={{ color: statusColor, fontWeight: 'bold' }}>
                          {displayStatus}
                        </span>
                      </div>
                    )}
                    {showNotes && <div className="port-service" style={rowStyleMerged}>{backup.notes}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract backup status data from scan results
 * Matches _Network_Backups_status from template2/Network.js:528-550
 * Only includes devices that have backups configured
 */
/**
 * Extract backup status data from scan results
 * Matches _Network_Backups_status from template2_doc/Network.js:167-187 EXACTLY
 */
export function extractBackupStatusData(data) {
  const backupData = [];

  if (!data) {
    console.log('[extractBackupStatusData] No data provided');
    return backupData;
  }

  // Handle case where data might be the network_devices array directly
  if (Array.isArray(data)) {
    console.log('[extractBackupStatusData] Data is array directly, treating as network_devices:', data.length);
    // Process as network_devices array
    const networkDevices = data;
    return processBackupData(networkDevices);
  }

  // The dataContext from getBlockData spreads scanData at top level, so network_devices should be directly accessible
  // Try: data.network_devices (top level from spread), data.scanData.network_devices, data.data.network_devices
  let networkDevices = null;

  // Check top level first (dataContext spreads scanData)
  if (data.network_devices && Array.isArray(data.network_devices)) {
    networkDevices = data.network_devices;
    console.log('[extractBackupStatusData] Found network_devices at top level:', networkDevices.length);
  } else if (data.scanData?.network_devices && Array.isArray(data.scanData.network_devices)) {
    networkDevices = data.scanData.network_devices;
    console.log('[extractBackupStatusData] Found network_devices in scanData:', networkDevices.length);
  } else if (data.data?.network_devices && Array.isArray(data.data.network_devices)) {
    networkDevices = data.data.network_devices;
    console.log('[extractBackupStatusData] Found network_devices in data.data:', networkDevices.length);
  } else {
    console.log('[extractBackupStatusData] No network_devices found.');
    console.log('[extractBackupStatusData] Data type:', typeof data);
    console.log('[extractBackupStatusData] Is array:', Array.isArray(data));
    console.log('[extractBackupStatusData] Data keys:', Object.keys(data || {}).slice(0, 20));
    if (data.scanData) {
      console.log('[extractBackupStatusData] scanData keys:', Object.keys(data.scanData || {}).slice(0, 20));
    }
    // Try to find network_devices with case-insensitive search
    const allKeys = Object.keys(data || {});
    const networkKey = allKeys.find(key => key.toLowerCase() === 'network_devices' || key.toLowerCase().includes('network'));
    if (networkKey && Array.isArray(data[networkKey])) {
      networkDevices = data[networkKey];
      console.log('[extractBackupStatusData] Found network_devices via case-insensitive search:', networkKey, networkDevices.length);
    } else {
      return backupData;
    }
  }

  return processBackupData(networkDevices);
}

/**
 * Process network devices to extract backup data
 * Matches _Network_Backups_status from template2_doc/Network.js:167-187 EXACTLY
 */
function processBackupData(networkDevices) {
  const backupData = [];

  // Match backend EXACTLY: _Network_Backups_status from template2_doc/Network.js:167-187
  // const rows = []
  // let nobackup = 0
  // let total = devices.length
  // try {
  //     for (const device of devices) {
  //         const backups = device?.system?.['backups'];
  //         if (!backups) continue;
  //         rows.push([device.ip, backups['last_backup'] || '-', backups['status'] || '-', backups['notes'] || '-'])
  //         if (!(backups['notes'] || '').includes("Backup completed.")) {
  //             nobackup++
  //         }
  //     }
  // } catch (error) {
  //     console.log("Error _Network_Backups_status", error);
  // }
  // const avg = total > 0 ? Math.round(100 * nobackup / total) : 0
  // return [rows, nobackup, total, avg]

  try {
    for (const device of networkDevices) {
      // Match backend: const backups = device?.system?.['backups'];
      const backups = device?.system?.['backups'];

      // Match backend: if (!backups) continue;
      if (!backups) continue;

      const deviceIp = device.ip || device.IP || '';
      const hostname = device.hostname || device.Hostname || device.name || device.Name || '-';
      const lastBackup = backups['last_backup'] || backups['LastBackup'] || '-';
      const backupStatus = backups['status'] || backups['Status'] || '-';
      const backupNotes = backups['notes'] || backups['Notes'] || '-';

      backupData.push({
        device: deviceIp,
        hostname: hostname,
        last_backup: lastBackup,
        status: backupStatus,
        notes: backupNotes
      });
    }

    console.log('[processBackupData] Extracted', backupData.length, 'backup records from', networkDevices.length, 'total devices');
  } catch (error) {
    console.log("Error processBackupData", error);
    console.error("Error processBackupData stack:", error.stack);
  }

  return backupData;
}

/**
 * Network - Device Age by Device Table
 * Shows age of network devices
 */
/**
 * Extract Network Device Age data
 */
export function extractNetworkDeviceAgeData(data) {
  const scanDataSource = data?.scanData || data?.data || data;
  const networkDevices = scanDataSource?.network_devices || [];
  const rows = [];

  networkDevices.forEach(device => {
    const system = device.system || {};
    const installDate = system.install_date || system.InstallDate || '';

    let age = 'Unknown';
    let status = 'Unknown';

    if (installDate) {
      const installYear = new Date(installDate).getFullYear();
      const currentYear = new Date().getFullYear();
      const deviceAge = currentYear - installYear;

      age = `${deviceAge} years`;

      if (deviceAge >= 5) status = 'Critical';
      else if (deviceAge >= 3) status = 'Warning';
      else status = 'Good';
    }

    rows.push({
      device: device.ip || device.hostname || '',
      install_date: installDate,
      age,
      status
    });
  });

  return rows;
}

/**
 * Network - Device Age by Device Table
 */
export const NetworkDeviceAgeTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'Device': 'showDevice', 'Install Date': 'showInstallDate', 'Age': 'showAge', 'Status': 'showStatus' });
  const {
    title = 'Device Age by Device',
    showDevice = true,
    showInstallDate = true,
    showAge = true,
    showStatus = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'network-device-age-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const _allAge_raw = extractNetworkDeviceAgeData(data);
  const allAge = applyTableSort(
    applyTableFilters(_allAge_raw, config.tableFilters || config?.tableFilters),
    config.tableSort
  );

  const displayAge = pagination
    ? (() => {
      const slice = getPaginationSlice(pagination);
      return allAge.slice(slice.startIndex, slice.endIndex);
    })()
    : allAge;

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showDevice, showInstallDate, showAge, showStatus].filter(Boolean).length;
  const templateColumns = Array(visibleColumns).fill('1fr').join(' ');

  const statusColors = {
    'Critical': theme?.statusColors?.red || '#d71920',
    'Warning': theme?.statusColors?.yellow || '#fdbc13',
    'Good': theme?.statusColors?.green || '#6fca42',
    'Unknown': '#9ca3af'
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showDevice && <div style={headerStyle}>{getDisplayHeader(config, 'Device')}</div>}
            {showInstallDate && <div style={headerStyle}>{getDisplayHeader(config, 'Install Date')}</div>}
            {showAge && <div style={headerStyle}>{getDisplayHeader(config, 'Age')}</div>}
            {showStatus && <div style={headerStyle}>{getDisplayHeader(config, 'Status')}</div>}
          </div>
          <div className="open-ports-body">
            {displayAge.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No device age data found.</p>
              </div>
            ) : (
              displayAge.map((item, idx) => (
                <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                  {showDevice && <div className="port-ip" style={rowStyleMerged}>{item.device}</div>}
                  {showInstallDate && <div className="port-number" style={rowStyleMerged}>{item.install_date || '-'}</div>}
                  {showAge && <div className="port-service" style={rowStyleMerged}>{item.age}</div>}
                  {showStatus && (
                    <div style={rowStyleMerged}>
                      <span style={{ color: statusColors[item.status] || '#666', fontWeight: 'bold' }}>
                        {item.status}
                      </span>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Network - Installed Software Table
 * Shows installed software across network devices
 */
/**
 * Extract Network Installed Software data
 */
export function extractNetworkInstalledSoftwareData(data) {
  const scanDataSource = data?.scanData || data?.data || data;
  const networkDevices = scanDataSource?.network_devices || [];
  const rows = [];

  // Common software to track
  const commonSoftware = ['Chrome', 'Firefox', 'Adobe', 'Java', 'Office'];

  networkDevices.forEach(device => {
    const system = device.system || {};
    const softwares = system.softwares || system.installed_software || [];

    softwares.forEach(software => {
      const name = software.Name || software.name || '';
      const version = software.Version || software.version || '';

      // Check if it's common software
      const isCommon = commonSoftware.some(sw => name.includes(sw));

      if (isCommon || commonSoftware.length === 0) {
        // Simple outdated check (in real scenario, would check against latest versions)
        let status = 'Up to Date';
        if (software.Outdated || version.includes('old')) {
          status = 'Outdated';
        }

        rows.push({
          device: device.ip || device.hostname || '',
          name,
          installed_version: version,
          status
        });
      }
    });
  });

  return rows;
}

/**
 * Network - Installed Software Table
 */
export const NetworkInstalledSoftwareTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'Device': 'showDevice', 'Name': 'showName', 'Installed Version': 'showVersion', 'Status': 'showStatus' });
  const {
    title = 'Installed Software',
    showDevice = true,
    showName = true,
    showVersion = true,
    showStatus = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'network-installed-software-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const _allSoftware_raw = extractNetworkInstalledSoftwareData(data);
  const allSoftware = applyTableSort(
    applyTableFilters(_allSoftware_raw, config.tableFilters || config?.tableFilters),
    config.tableSort
  );

  const displaySoftware = pagination
    ? (() => {
      const slice = getPaginationSlice(pagination);
      return allSoftware.slice(slice.startIndex, slice.endIndex);
    })()
    : allSoftware;

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showDevice, showName, showVersion, showStatus].filter(Boolean).length;
  const templateColumns = Array(visibleColumns).fill('1fr').join(' ');

  const statusColors = {
    'Outdated': theme?.statusColors?.yellow || '#fdbc13',
    'Up to Date': theme?.statusColors?.green || '#6fca42'
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showDevice && <div style={headerStyle}>{getDisplayHeader(config, 'Device')}</div>}
            {showName && <div style={headerStyle}>{getDisplayHeader(config, 'Name')}</div>}
            {showVersion && <div style={headerStyle}>{getDisplayHeader(config, 'Installed Version')}</div>}
            {showStatus && <div style={headerStyle}>{getDisplayHeader(config, 'Status')}</div>}
          </div>
          <div className="open-ports-body">
            {displaySoftware.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No installed software found.</p>
              </div>
            ) : (
              displaySoftware.map((item, idx) => (
                <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                  {showDevice && <div className="port-ip" style={rowStyleMerged}>{item.device}</div>}
                  {showName && <div className="port-ip" style={rowStyleMerged}>{item.name}</div>}
                  {showVersion && <div className="port-number" style={rowStyleMerged}>{item.installed_version}</div>}
                  {showStatus && (
                    <div style={rowStyleMerged}>
                      <span style={{ color: statusColors[item.status] || '#666', fontWeight: 'bold' }}>
                        {item.status}
                      </span>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Network - OS Versions Table
 * Shows operating system versions across network with risk status
 * Aggregated by OS name with count and device list
 * Matches analyzeNetworkOS logic from template2/Network.js
 * Widget auto-sizes to fit all content
 */
/**
 * Network - OS Versions Table
 */
export const NetworkOSVersionsTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const lookoutField = config.lookoutField || 'Device';
  
  const defaultHidden = lookoutField === 'OS Name'
    ? { 'OS Name': 'showOSName', 'Count': 'showCount', 'Devices': 'showDevices', 'Status': 'showStatus' }
    : { 'Device': 'showDevice', 'Hostname': 'showHostname', 'OS Name': 'showOSName', 'Last User Login': 'showLastUserLogin', 'Status': 'showStatus' };
    
  const hiddenOverrides = applyHiddenColumns(config, defaultHidden);
  const isAggregated = lookoutField === 'OS Name';

  const {
    title = 'OS Versions',
    showDevice = true,
    showHostname = true,
    showOSName = true,
    showLastUserLogin = true,
    showStatus = true,
    showCount = true,
    showDevices = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'network-os-versions-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const _allOSData_raw = extractOSVersionsData(data, config);
  const allOSData = applyTableSort(
    applyTableFilters(_allOSData_raw, config.tableFilters || config?.tableFilters),
    config.tableSort
  );

  const displayOS = pagination
    ? (() => {
      const slice = getPaginationSlice(pagination);
      return allOSData.slice(slice.startIndex, slice.endIndex);
    })()
    : allOSData;

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyleMerged = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  let templateColumns = '';
  if (isAggregated) {
    const columns = [
      showOSName ? '2.5fr' : '',
      showCount ? '0.8fr' : '',
      showDevices ? '2fr' : '',
      showStatus ? '1fr' : ''
    ].filter(Boolean);
    templateColumns = columns.join(' ') || '1fr';
  } else {
    const columns = [
      showDevice ? '1fr' : '',
      showHostname ? '1.2fr' : '',
      showOSName ? '2fr' : '',
      showLastUserLogin ? '2.5fr' : '',
      showStatus ? '1fr' : ''
    ].filter(Boolean);
    templateColumns = columns.join(' ') || '1fr';
  }

  const riskColors = {
    'Critical': theme?.statusColors?.red || '#d71920',
    'Moderate': theme?.statusColors?.yellow || '#fdbc13',
    'Normal': theme?.statusColors?.green || '#6fca42',
    'Unknown': '#666'
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {isAggregated ? (
              <>
                {showOSName && <div style={headerStyleMerged}>{getDisplayHeader(config, 'OS Name')}</div>}
                {showCount && <div style={headerStyleMerged}>{getDisplayHeader(config, 'Count')}</div>}
                {showDevices && <div style={headerStyleMerged}>{getDisplayHeader(config, 'Devices')}</div>}
                {showStatus && <div style={headerStyleMerged}>{getDisplayHeader(config, 'Status')}</div>}
              </>
            ) : (
              <>
                {showDevice && <div style={headerStyleMerged}>{getDisplayHeader(config, 'Device')}</div>}
                {showHostname && <div style={headerStyleMerged}>{getDisplayHeader(config, 'Hostname')}</div>}
                {showOSName && <div style={headerStyleMerged}>{getDisplayHeader(config, 'OS Name')}</div>}
                {showLastUserLogin && <div style={headerStyleMerged}>{getDisplayHeader(config, 'Last User Login')}</div>}
                {showStatus && <div style={headerStyleMerged}>{getDisplayHeader(config, 'Status')}</div>}
              </>
            )}
          </div>
          <div className="open-ports-body">
            {displayOS.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No OS devices found.</p>
              </div>
            ) : (
              displayOS.map((os, idx) => (
                <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                  {isAggregated ? (
                    <>
                      {showOSName && <div className="port-service" style={rowStyleMerged}>{os.os_name}</div>}
                      {showCount && <div className="port-number" style={rowStyleMerged}>{os.count}</div>}
                      {showDevices && <div className="port-service" style={rowStyleMerged}>{os.devices}</div>}
                      {showStatus && (
                        <div style={rowStyleMerged}>
                          <span style={{ color: riskColors[os.status] || '#666', fontWeight: 'bold' }}>
                            {os.status}
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {showDevice && <div className="port-ip" style={rowStyleMerged}>{os.device}</div>}
                      {showHostname && <div className="port-ip" style={rowStyleMerged}>{os.hostname}</div>}
                      {showOSName && <div className="port-service" style={rowStyleMerged}>{os.os_name}</div>}
                      {showLastUserLogin && <div className="port-service" style={rowStyleMerged}>{os.last_user_login}</div>}
                      {showStatus && (
                        <div style={rowStyleMerged}>
                          <span style={{ color: riskColors[os.status] || '#666', fontWeight: 'bold' }}>
                            {os.status}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Analyze Network Firewall status
 * Matches _Network_Firewall_status from template2/Network.js:1769-1801
 */
function analyzeNetworkFirewall(devices) {
  const rows = [];
  let notFullyEnabledCount = 0;
  const totalDevices = devices.length || 0;

  try {
    for (const device of devices) {
      const name = device.ip || device.IP || 'Unknown Device';
      const hostname = device.hostname || device.Hostname || device.name || device.Name || '-';
      const firewall = device?.system?.Firewall;

      if (!firewall || typeof firewall !== 'object') {
        notFullyEnabledCount++;
        continue;
      }

      const domain = firewall.Domain === true;
      const privateNet = firewall.Private === true;
      const publicNet = firewall.Public === true;
      const profileSummary = `Domain: ${domain ? 'Enabled' : 'Disabled'}, Private: ${privateNet ? 'Enabled' : 'Disabled'}, Public: ${publicNet ? 'Enabled' : 'Disabled'}`;
      const enabledCount = [domain, privateNet, publicNet].filter(Boolean).length;

      let status = 'Disabled';
      if (enabledCount === 3) {
        status = 'Fully Enabled';
      } else if (enabledCount > 0) {
        status = 'Partially Enabled';
        notFullyEnabledCount++;
      } else {
        notFullyEnabledCount++;
      }

      rows.push([name, hostname, profileSummary, status]);
    }
  } catch (error) {
    console.log('Error analyzeNetworkFirewall', error);
    return null;
  }

  const avg = totalDevices > 0 ? Math.round(100 * notFullyEnabledCount / totalDevices) : 0;
  return [rows, notFullyEnabledCount, totalDevices, avg];
}

/**
 * Extract firewall status data from scan results
 * Returns array of { device, profiles, status }
 */
export function extractFirewallStatusData(data) {
  const firewallData = [];

  if (!data) return firewallData;

  // Extract from raw scan data
  const scanDataSource = data.scanData || data.data || data;
  const networkDevices = scanDataSource?.network_devices || [];

  // Filter to only devices that have system data
  const validDevices = networkDevices.filter(device =>
    device?.system && device.system !== "WMI Error"
  );

  const [rows] = analyzeNetworkFirewall(validDevices);
  if (!rows) return firewallData;

  return rows.map(row => ({
    device: row[0],
    hostname: row[1],
    profiles: row[2],
    status: row[3]
  }));
}

/**
 * Network Firewall Status by Device Table Widget
 * Shows firewall status for each device with profiles
 * Matches Network_Firewall_status table from template2/Network.js:1821-1833
 * Widget auto-sizes to fit all content
 */
/**
 * Network Firewall Status by Device Table Widget
 */
export const NetworkFirewallStatusTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'Device': 'showDevice', 'Hostname': 'showHostname', 'Firewall Profiles': 'showProfiles', 'Status': 'showStatus' });
  const {
    title = 'Firewall Status by Device',
    showDevice = true,
    showHostname = true,
    showProfiles = true,
    showStatus = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'network-firewall-status-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const _allFirewall_raw = extractFirewallStatusData(data);
  const allFirewall = applyTableSort(
    applyTableFilters(_allFirewall_raw, config.tableFilters || config?.tableFilters),
    config.tableSort
  );

  const displayFirewall = pagination
    ? (() => {
      const slice = getPaginationSlice(pagination);
      return allFirewall.slice(slice.startIndex, slice.endIndex);
    })()
    : allFirewall;

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showDevice, showHostname, showProfiles, showStatus].filter(Boolean).length;
  const templateColumns = visibleColumns === 4 
    ? '1fr 1fr 2.5fr 1.5fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  const statusColors = {
    'Fully Enabled': theme?.statusColors?.green || '#6fca42',
    'Partially Enabled': theme?.statusColors?.yellow || '#fdbc13',
    'Disabled': theme?.statusColors?.red || '#d71920'
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showDevice && <div style={headerStyle}>{getDisplayHeader(config, 'Device')}</div>}
            {showHostname && <div style={headerStyle}>{getDisplayHeader(config, 'Hostname')}</div>}
            {showProfiles && <div style={headerStyle}>{getDisplayHeader(config, 'Firewall Profiles')}</div>}
            {showStatus && <div style={headerStyle}>{getDisplayHeader(config, 'Status')}</div>}
          </div>
          <div className="open-ports-body">
            {displayFirewall.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No firewall data found.</p>
              </div>
            ) : (
              displayFirewall.map((item, idx) => (
                <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                  {showDevice && <div className="port-ip" style={rowStyleMerged}>{item.device}</div>}
                  {showHostname && <div className="port-ip" style={rowStyleMerged}>{item.hostname}</div>}
                  {showProfiles && <div className="port-service" style={rowStyleMerged}>{item.profiles}</div>}
                  {showStatus && (
                    <div style={rowStyleMerged}>
                      <span style={{ color: statusColors[item.status] || '#666', fontWeight: 'bold' }}>
                        {item.status}
                      </span>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
    </div>
  );
};

export function extractOSVersionsData(data, config = {}) {
  if (!data) return [];

  const lookoutField = config.lookoutField || 'Device';
  const scanDataSource = data.scanData || data.data || data;
  const networkDevices = scanDataSource?.network_devices || [];
  const adComputers = scanDataSource?.['Ad Scan']?.Computers || [];
  const adInactive = scanDataSource?.['Ad Scan']?.inactive_computers || [];

  const normalizeHostname = (name) => {
    if (!name || typeof name !== 'string') return '';
    return name.split('.')[0].split(':')[0].toUpperCase();
  };

  const getOSRisk = (os) => {
    if (!os) return 'Unknown';
    if (/Windows XP|Vista|Windows 7|Windows 8(?!\.1)|Server 2003|Server 2008|Server 2012/.test(os)) {
      return 'Critical';
    } else if (/Windows 10|Server 2016|Server 2019/.test(os)) {
      return 'Moderate';
    } else if (/Windows 11|Server 2022/.test(os)) {
      return 'Normal';
    }
    return 'Unknown';
  };

  const deviceMap = new Map();

  // 1. Process Network Devices
  networkDevices.forEach(device => {
    const rawOS = device?.system?.['OS'] || device?.device_type || '';
    if (!rawOS) return;

    const hostname = device.hostname || device.Hostname || device.host || device.Name || '';
    const ip = device.ip || device.IP || '';
    const normalizedHost = normalizeHostname(hostname);
    const key = normalizedHost || ip;

    if (key) {
      deviceMap.set(key, {
        device: ip,
        hostname: hostname || ip || '-',
        os_name: rawOS,
        active_user: device.system?.['Active User'] || '',
        last_logon: device.system?.last_logon || '',
        source: 'network'
      });
    }
  });

  // 2. Process AD Computers (Merge or Add)
  const allADComputers = [...adComputers, ...adInactive];
  allADComputers.forEach(item => {
    // Normalize item.last_logon if it exists
    let logon = item.last_logon || '';
    if (logon && typeof logon === 'string' && logon.includes('T')) {
      logon = logon.split('T')[0];
    }

    const rawOS = item.os || item.OS || item.operating_system || '';
    if (!rawOS) return;

    const name = item.name || '';
    const host = item.Host || item.host || '';
    const normalizedName = normalizeHostname(name);
    const normalizedHost = normalizeHostname(host);
    const key = normalizedName || normalizedHost;

    if (key && deviceMap.has(key)) {
      const existing = deviceMap.get(key);
      existing.last_logon = existing.last_logon || logon || '';
      // If AD OS is more specific or different, we could update if we want, but keeping current for now
    } else if (key) {
      deviceMap.set(key, {
        device: item.ip || item.IP || '-',
        hostname: name || host || '-',
        os_name: rawOS,
        active_user: '',
        last_logon: logon,
        source: 'ad'
      });
    }
  });

  const processedData = Array.from(deviceMap.values()).map(dev => {
    const risk = getOSRisk(dev.os_name);
    
    // Combine User and Login
    const user = dev.active_user;
    const login = dev.last_logon;
    let combined = '-';
    if (user && login) {
      combined = `${user} (${login})`;
    } else {
      combined = user || login || '-';
    }

    return {
      ...dev,
      last_user_login: combined,
      status: risk
    };
  });

  if (lookoutField === 'OS Name') {
    const aggregated = {};
    processedData.forEach(item => {
      const OS = item.os_name;
      if (!aggregated[OS]) {
        aggregated[OS] = {
          os_name: OS,
          count: 0,
          devices: [],
          status: item.status
        };
      }
      aggregated[OS].count++;
      aggregated[OS].devices.push(item.hostname || item.device);
    });

    return Object.values(aggregated).map(item => ({
      ...item,
      devices: item.devices.join(', ')
    }));
  }

  return processedData;
}

/**
 * Network - Non-Default Services Risk Breakdown by State Table
 * Shows risk breakdown by service state
 * Matches _Network_Services_status rows from template2/Network.js:1142-1154
 * This widget shows the breakdown table with: State, Total Services, Severity, Explanation
 */
/**
 * Network - Non-Default Services Table
 */
export const NetworkNonDefaultServicesTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'IP Address': 'showIP', 'Hostname': 'showHostname', 'Name': 'showName', 'Display Name': 'showDisplayName', 'State': 'showState', 'Start Mode': 'showStartMode' });
  const {
    title = 'Non-Default Services Found',
    showNo = true,
    showIP = true,
    showHostname = true,
    showName = true,
    showDisplayName = true,
    showState = true,
    showStartMode = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'network-non-default-services-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allServicesData = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractDetailedServicesData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayServices = useMemo(() => {
    if (!pagination) return allServicesData;
    const slice = getPaginationSlice(pagination);
    return allServicesData.slice(slice.startIndex, slice.endIndex);
  }, [allServicesData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showNo, showIP, showHostname, showName, showDisplayName, showState, showStartMode].filter(Boolean).length;
  const templateColumns = visibleColumns === 7 
    ? '0.5fr 1fr 1fr 1.5fr 2fr 1fr 1fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  const stateColors = {
    'Running': theme?.statusColors?.red || '#d71920',
    'Stopped': theme?.statusColors?.yellow || '#fdbc13',
    'Disabled': theme?.statusColors?.green || '#6fca42'
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showIP && <div style={headerStyle}>{getDisplayHeader(config, 'IP Address')}</div>}
            {showHostname && <div style={headerStyle}>{getDisplayHeader(config, 'Hostname')}</div>}
            {showName && <div style={headerStyle}>{getDisplayHeader(config, 'Name')}</div>}
            {showDisplayName && <div style={headerStyle}>{getDisplayHeader(config, 'Display Name')}</div>}
            {showState && <div style={headerStyle}>{getDisplayHeader(config, 'State')}</div>}
            {showStartMode && <div style={headerStyle}>{getDisplayHeader(config, 'Start Mode')}</div>}
          </div>
          <div className="open-ports-body">
            {displayServices.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No non-default services found.</p>
              </div>
            ) : (
              displayServices.map((service, idx) => (
                <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                  {showNo && <div className="port-number" style={rowStyleMerged}>{idx + 1}</div>}
                  {showIP && <div className="port-ip" style={rowStyleMerged}>{service.ip}</div>}
                  {showHostname && <div className="port-ip" style={rowStyleMerged}>{service.hostname}</div>}
                  {showName && <div className="port-service" style={rowStyleMerged}>{service.name}</div>}
                  {showDisplayName && <div className="port-service" style={rowStyleMerged}>{service.displayName}</div>}
                  {showState && (
                    <div style={rowStyleMerged}>
                      <span style={{ color: stateColors[service.state] || '#666', fontWeight: 'bold' }}>
                        {service.state}
                      </span>
                    </div>
                  )}
                  {showStartMode && <div className="port-service" style={rowStyleMerged}>{service.startMode}</div>}
                </div>
              ))
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Network - All Open Ports Table
 * Shows all open ports found on network devices
 */
/**
 * Extract Network All Open Ports data
 */
export function extractNetworkAllOpenPortsData(data) {
  const scanDataSource = data?.scanData || data?.data || data;
  const networkDevices = scanDataSource?.network_devices || [];
  const rows = [];
  const sensitivePortsList = [21, 22, 23, 25, 53, 80, 110, 135, 139, 143, 443, 445, 1433, 1521, 3306, 3389, 5432, 5900, 8080];

  networkDevices.forEach(device => {
    const openPorts = device.open_ports || [];
    if (Array.isArray(openPorts)) {
      openPorts.forEach(port => {
        const portNum = typeof port === 'object' ? port.port : port;
        const isSensitive = sensitivePortsList.includes(portNum);
        rows.push({
          ip: device.ip || '',
          hostname: device.hostname || device.host || device.name || '',
          port: portNum,
          is_sensitive: isSensitive ? 'Yes' : 'No',
          service: typeof port === 'object' ? port.service : ''
        });
      });
    } else if (typeof openPorts === 'object' && openPorts !== null) {
      Object.keys(openPorts).forEach(portKey => {
        const portNum = parseInt(portKey);
        if (!isNaN(portNum)) {
          const isSensitive = sensitivePortsList.includes(portNum);
          rows.push({
            ip: device.ip || '',
            hostname: device.hostname || device.host || device.name || '',
            port: portNum,
            is_sensitive: isSensitive ? 'Yes' : 'No',
            service: openPorts[portKey]?.service || ''
          });
        }
      });
    }
  });

  return rows;
}

/**
 * Network - All Open Ports Table
 */
export const NetworkAllOpenPortsTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'IP Address': 'showIP', 'Hostname': 'showHostname', 'Port': 'showPort', 'Is Sensitive': 'showIsSensitive', 'Service': 'showService' });
  const {
    title = 'All Open Ports Found',
    showNo = true,
    showIP = true,
    showHostname = true,
    showPort = true,
    showIsSensitive = true,
    showService = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'network-all-open-ports-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allPortsData = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractNetworkAllOpenPortsData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayPorts = useMemo(() => {
    if (!pagination) return allPortsData;
    const slice = getPaginationSlice(pagination);
    return allPortsData.slice(slice.startIndex, slice.endIndex);
  }, [allPortsData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showNo, showIP, showHostname, showPort, showIsSensitive, showService].filter(Boolean).length;
  const templateColumns = visibleColumns === 6 
    ? '0.5fr 1.5fr 1.5fr 1fr 1fr 1.5fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showIP && <div style={headerStyle}>{getDisplayHeader(config, 'IP Address')}</div>}
            {showHostname && <div style={headerStyle}>{getDisplayHeader(config, 'Hostname')}</div>}
            {showPort && <div style={headerStyle}>{getDisplayHeader(config, 'Port')}</div>}
            {showIsSensitive && <div style={headerStyle}>{getDisplayHeader(config, 'Is Sensitive')}</div>}
            {showService && <div style={headerStyle}>{getDisplayHeader(config, 'Service')}</div>}
          </div>
          <div className="open-ports-body">
            {displayPorts.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No open ports found.</p>
              </div>
            ) : (
              displayPorts.map((port, idx) => {
                const startIndex = pagination && pagination.startIndex !== undefined
                  ? pagination.startIndex
                  : (pagination ? (pagination.currentPage - 1) * pagination.itemsPerPage : 0);
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div className="port-number" style={rowStyleMerged}>{actualNumber}</div>}
                    {showIP && <div className="port-ip" style={rowStyleMerged}>{port.ip}</div>}
                    {showHostname && <div className="port-ip" style={rowStyleMerged}>{port.hostname}</div>}
                    {showPort && <div className="port-number" style={rowStyleMerged}>{port.port}</div>}
                    {showIsSensitive && (
                      <div style={rowStyleMerged}>
                        <span style={{ 
                          color: port.is_sensitive === 'Yes' ? (theme?.statusColors?.red || '#d71920') : 'inherit', 
                          fontWeight: port.is_sensitive === 'Yes' ? 'bold' : 'normal' 
                        }}>
                          {port.is_sensitive}
                        </span>
                      </div>
                    )}
                    {showService && <div className="port-service" style={rowStyleMerged}>{port.service}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract sensitive open ports data from scan results
 * Returns array of { ip, port, service }
 * EXPORTED so ReportBuilder can use the same logic for counting
 */
export function extractSensitiveOpenPortsData(data) {
  const rows = [];
  const seenPorts = new Set(); // Track seen IP:Port combinations to prevent duplicates

  if (!data) return rows;

  // Handle wrapped data structure (scanData, data, or direct)
  const scanDataSource = data?.scanData || data?.data || data;
  const networkDevices = scanDataSource?.network_devices || [];

  // Sensitive ports with service names
  const sensitivePorts = {
    21: 'FTP',
    22: 'SSH',
    23: 'Telnet',
    25: 'SMTP',
    53: 'DNS',
    80: 'HTTP',
    110: 'POP3',
    135: 'RPC',
    139: 'NetBIOS',
    143: 'IMAP',
    443: 'HTTPS',
    445: 'SMB',
    1433: 'SQL Server',
    1521: 'Oracle',
    3306: 'MySQL',
    3389: 'RDP',
    5432: 'PostgreSQL',
    5900: 'VNC',
    8080: 'HTTP-Alt'
  };

  networkDevices.forEach(device => {
    const deviceIP = device.ip || '';
    const deviceHostname = device.hostname || device.host || device.name || '';
    const openPorts = device.open_ports || [];

    // Handle different data structures:
    // 1. Array of port numbers: [80, 443]
    // 2. Array of port objects: [{port: 80, service: "HTTP"}, {port: 443}]
    // 3. Object with port numbers as keys: {"80": {...}, "443": {...}}

    if (Array.isArray(openPorts)) {
      openPorts.forEach(port => {
        const portNum = typeof port === 'object' ? (port.port || port.portNum) : parseInt(port);

        if (!isNaN(portNum) && sensitivePorts[portNum]) {
          const uniqueKey = `${deviceIP}:${portNum}`;
          if (!seenPorts.has(uniqueKey)) {
            seenPorts.add(uniqueKey);
            rows.push({
              ip: deviceIP,
              hostname: deviceHostname,
              port: portNum,
              service: sensitivePorts[portNum]
            });
          }
        }
      });
    } else if (typeof openPorts === 'object' && openPorts !== null) {
      // Handle object structure: {"80": {...}, "443": {...}}
      Object.keys(openPorts).forEach(portKey => {
        const portNum = parseInt(portKey);

        if (!isNaN(portNum) && sensitivePorts[portNum]) {
          const uniqueKey = `${deviceIP}:${portNum}`;
          if (!seenPorts.has(uniqueKey)) {
            seenPorts.add(uniqueKey);
            rows.push({
              ip: deviceIP,
              hostname: deviceHostname,
              port: portNum,
              service: sensitivePorts[portNum]
            });
          }
        }
      });
    }
  });

  return rows;
}

/**
 * Network - Sensitive Open Ports Table
 * Shows only sensitive open ports
 * Widget auto-sizes to fit all content
 */
/**
 * Network - Sensitive Open Ports Table
 */
export const NetworkSensitiveOpenPortsTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'IP Address': 'showIP', 'Hostname': 'showHostname', 'Port': 'showPort', 'Is Sensitive': 'showIsSensitive', 'Service': 'showService' });
  const {
    title = 'Sensitive Open Ports',
    showNo = true,
    showIP = true,
    showHostname = true,
    showPort = true,
    showIsSensitive = true,
    showService = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'network-sensitive-open-ports-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allPortsData = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractSensitiveOpenPortsData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayPorts = useMemo(() => {
    if (!pagination) return allPortsData;
    const slice = getPaginationSlice(pagination);
    return allPortsData.slice(slice.startIndex, slice.endIndex);
  }, [allPortsData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showNo, showIP, showHostname, showPort, showIsSensitive, showService].filter(Boolean).length;
  const templateColumns = visibleColumns === 6 
    ? '0.5fr 1.5fr 1.5fr 1fr 1fr 1.5fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showIP && <div style={headerStyle}>{getDisplayHeader(config, 'IP Address')}</div>}
            {showHostname && <div style={headerStyle}>{getDisplayHeader(config, 'Hostname')}</div>}
            {showPort && <div style={headerStyle}>{getDisplayHeader(config, 'Port')}</div>}
            {showIsSensitive && <div style={headerStyle}>{getDisplayHeader(config, 'Is Sensitive')}</div>}
            {showService && <div style={headerStyle}>{getDisplayHeader(config, 'Service')}</div>}
          </div>
          <div className="open-ports-body">
            {displayPorts.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No sensitive open ports found.</p>
              </div>
            ) : (
              displayPorts.map((port, idx) => {
                const startIndex = pagination && pagination.startIndex !== undefined
                  ? pagination.startIndex
                  : (pagination ? (pagination.currentPage - 1) * pagination.itemsPerPage : 0);
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div className="port-number" style={rowStyleMerged}>{actualNumber}</div>}
                    {showIP && <div className="port-ip" style={rowStyleMerged}>{port.ip}</div>}
                    {showHostname && <div className="port-ip" style={rowStyleMerged}>{port.hostname}</div>}
                    {showPort && <div className="port-number" style={rowStyleMerged}>{port.port}</div>}
                    {showIsSensitive && (
                      <div className="port-sensitive" style={rowStyleMerged}>
                        <span style={{ color: theme?.statusColors?.red || '#d71920', fontWeight: 'bold' }}>
                          Yes
                        </span>
                      </div>
                    )}
                    {showService && <div className="port-service" style={rowStyleMerged}>{port.service}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Network - Antivirus Status Table
 * Shows antivirus status for all network devices
 */
export function extractAntivirusStatusData(data) {
  const avData = [];
  if (!data) return avData;

  // Handle wrapped data structure (scanData, data, or direct)
  const scanDataSource = data?.scanData || data?.data || data;
  const networkDevices = scanDataSource?.network_devices || [];

  networkDevices.forEach(device => {
    const ip = device.ip || '';
    const hostname = device.hostname || device.host || '';
    // Use uppercase key to match actual scan data structure (from Network.js L198)
    const avs = device?.system?.['Antivirus'];

    // If antivirus data is missing or empty, device is not protected
    if (!Array.isArray(avs) || avs.length === 0) {
      avData.push({
        'IP Address': ip,
        'Hostname': hostname,
        'Antivirus': 'Not Found',
        'Path': 'N/A',
        'Status': 'Not Protected'
      });
    } else {
      // Device has antivirus data - mark as Protected
      // Handle multiple antivirus entries if present
      avs.forEach(av => {
        // av format: [Name, Path, (optional timestamp/other)]
        const avName = Array.isArray(av) ? (av[0] || 'Unknown') : (typeof av === 'string' ? av : 'Unknown');
        const avPath = Array.isArray(av) ? (av[1] || 'N/A') : 'N/A';
        
        avData.push({
          'IP Address': ip,
          'Hostname': hostname,
          'Antivirus': avName,
          'Path': avPath,
          'Status': 'Protected'
        });
      });
    }
  });

  return avData;
}

/**
 * Network - Antivirus Status Table
 * Shows antivirus status for all network devices
 */
/**
 * Network - Antivirus Status Table
 */
export const NetworkAntivirusStatusTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'IP Address': 'showIP', 'Hostname': 'showHostname', 'Antivirus': 'showAntivirus', 'Path': 'showPath', 'Status': 'showStatus' });
  const {
    title = 'Antivirus Status by Device',
    showNo = true,
    showIP = true,
    showHostname = true,
    showAntivirus = true,
    showPath = true,
    showStatus = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'network-antivirus-status-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const _allAVStatus_raw = extractAntivirusStatusData(data);
  const allAVStatus = applyTableSort(
    applyTableFilters(_allAVStatus_raw, config.tableFilters || config?.tableFilters),
    config.tableSort
  );

  const displayAVStatus = pagination
    ? (() => {
        const slice = getPaginationSlice(pagination);
        return allAVStatus.slice(slice.startIndex, slice.endIndex);
      })()
    : allAVStatus;

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showNo, showIP, showHostname, showAntivirus, showPath, showStatus].filter(Boolean).length;
  const templateColumns = visibleColumns === 6 
    ? '0.5fr 1fr 1fr 2fr 2fr 1.5fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  const statusColors = {
    'Not Protected': theme?.statusColors?.red || '#d71920',
    'Outdated': theme?.statusColors?.yellow || '#fdbc13',
    'Protected': theme?.statusColors?.green || '#6fca42'
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showIP && <div style={headerStyle}>{getDisplayHeader(config, 'IP Address')}</div>}
            {showHostname && <div style={headerStyle}>{getDisplayHeader(config, 'Hostname')}</div>}
            {showAntivirus && <div style={headerStyle}>{getDisplayHeader(config, 'Antivirus')}</div>}
            {showPath && <div style={headerStyle}>{getDisplayHeader(config, 'Path')}</div>}
            {showStatus && <div style={headerStyle}>{getDisplayHeader(config, 'Status')}</div>}
          </div>
          <div className="open-ports-body">
            {displayAVStatus.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No antivirus status data found.</p>
              </div>
            ) : (
              displayAVStatus.map((av, idx) => {
                const statusValue = av['Status'] || av.status;
                const startIndex = pagination && pagination.startIndex !== undefined
                  ? pagination.startIndex
                  : (pagination ? (pagination.currentPage - 1) * pagination.itemsPerPage : 0);
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div className="port-number" style={rowStyleMerged}>{actualNumber}</div>}
                    {showIP && <div className="port-ip" style={rowStyleMerged}>{av['IP Address'] || av.ip}</div>}
                    {showHostname && <div className="port-ip" style={rowStyleMerged}>{av['Hostname'] || av.hostname}</div>}
                    {showAntivirus && <div className="port-service" style={rowStyleMerged}>{av['Antivirus'] || av.antivirus}</div>}
                    {showPath && <div className="port-service" style={rowStyleMerged}>{av['Path'] || av.path}</div>}
                    {showStatus && (
                      <div style={rowStyleMerged}>
                        <span style={{ color: statusColors[statusValue] || '#666', fontWeight: 'bold' }}>
                          {statusValue}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Network - Encryption Status Table
 * Shows BitLocker/encryption status for network devices
 */
export const extractEncryptionStatusData = (inputData) => {
  const scanDataSource = inputData?.scanData || inputData?.data || inputData;
  const networkDevices = scanDataSource?.network_devices || [];
  const rows = [];

  networkDevices.forEach((device, index) => {
    const system = device.system || {};
    // Use uppercase key (from Network.js L244)
    const encryptionArray = system.Encryption;

    // Skip devices where Encryption key is completely undefined (not scanned)
    if (encryptionArray == undefined) {
      return; // forEach continue
    }

    // Determine encryption status and services list using Network.js logic (L247-L273)
    let isEncrypted = false;
    let servicesList = 'None';

    if (!Array.isArray(encryptionArray) || encryptionArray.length === 0) {
      // Empty array or not an array = Not Secure
      isEncrypted = false;
    } else {
      // Check each encryption entry: enc is [driveLetter, statusString]
      const encryptionNames = new Set();
      for (const enc of encryptionArray) {
        const status = enc[1] || '';
        if (status.toLowerCase().includes('(encrypted)')) {
          isEncrypted = true;
        }
        encryptionNames.add(status);
      }
      servicesList = [...encryptionNames].join(', ') || 'None';
    }

    // Format Last User Login
    let lastLoginDisplay = system['Active User'] || 'Unknown';
    if (system['Last User Login Time'] || system['Last Logon']) {
       lastLoginDisplay += ` (${system['Last User Login Time'] || system['Last Logon']})`;
    } else if (lastLoginDisplay !== 'Unknown' && lastLoginDisplay !== 'No Active User') {
       lastLoginDisplay += ' (Time Unavailable)';
    }

    rows.push({
      no: index + 1,
      ip: device.ip || '',
      hostname: device.hostname || '',
      services: servicesList,
      lastUserLogin: lastLoginDisplay,
      status: isEncrypted ? 'Encrypted' : 'Unencrypted'
    });
  });

  return rows;
};

/**
 * Network - Encryption Status Table
 * Shows BitLocker/encryption status for network devices
 */
/**
 * Network - Encryption Status Table
 */
export const NetworkEncryptionStatusTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'IP Address': 'showIP', 'Hostname': 'showHostname', 'Last User Login': 'showLastUserLogin', 'Services': 'showServices', 'Status': 'showStatus' });
  const {
    title = 'Encryption Status by Device',
    showNo = true,
    showIP = true,
    showHostname = true,
    showLastUserLogin = true,
    showServices = true,
    showStatus = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'network-encryption-status-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allData = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractEncryptionStatusData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayData = useMemo(() => {
    if (!pagination) return allData;
    const slice = getPaginationSlice(pagination);
    return allData.slice(slice.startIndex, slice.endIndex);
  }, [allData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showNo, showIP, showHostname, showLastUserLogin, showServices, showStatus].filter(Boolean).length;
  const templateColumns = visibleColumns === 6 
    ? '0.5fr 1fr 1fr 1fr 2fr 1fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showIP && <div style={headerStyle}>{getDisplayHeader(config, 'IP Address')}</div>}
            {showHostname && <div style={headerStyle}>{getDisplayHeader(config, 'Hostname')}</div>}
            {showLastUserLogin && <div style={headerStyle}>{getDisplayHeader(config, 'Last User Login')}</div>}
            {showServices && <div style={headerStyle}>{getDisplayHeader(config, 'Services')}</div>}
            {showStatus && <div style={headerStyle}>{getDisplayHeader(config, 'Status')}</div>}
          </div>
          <div className="open-ports-body">
            {displayData.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No encryption status data found.</p>
              </div>
            ) : (
              displayData.map((row, idx) => {
                const statusColor = row.status === 'Encrypted' ? (theme?.statusColors?.green || '#6fca42') : (theme?.statusColors?.red || '#d71920');
                const startIndex = pagination && pagination.startIndex !== undefined
                  ? pagination.startIndex
                  : (pagination ? (pagination.currentPage - 1) * pagination.itemsPerPage : 0);
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div className="port-number" style={rowStyleMerged}>{actualNumber}</div>}
                    {showIP && <div className="port-ip" style={rowStyleMerged}>{row.ip}</div>}
                    {showHostname && <div className="port-ip" style={rowStyleMerged}>{row.hostname}</div>}
                    {showLastUserLogin && <div className="port-service" style={rowStyleMerged}>{row.lastUserLogin}</div>}
                    {showServices && <div className="port-service" style={rowStyleMerged}>{row.services}</div>}
                    {showStatus && (
                      <div style={rowStyleMerged}>
                        <span style={{ color: statusColor, fontWeight: 'bold' }}>
                          {row.status}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Network - Admins with No Expiring Password Table
 * Shows admins with passwords that don't expire
 * Matches _Network_Admin_Account_status logic from template2_doc/Network.js
 * Widget auto-sizes to fit all content
 */
/**
 * Network - Admins with No Expiring Password Table
 */
export const NetworkAdminsNoExpiringPasswordTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'IP Address': 'showComputerIP', 'Hostname': 'showHostname', 'Username': 'showUsername', 'Account Type': 'showAccountType', 'Password Expires': 'showPasswordExpires' });
  const {
    title = 'Admins with no Expiring Password',
    showNo = true,
    showComputerIP = true,
    showHostname = true,
    showUsername = true,
    showAccountType = true,
    showPasswordExpires = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'network-admins-no-expiring-password-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allAdminsData = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractAdminsNoExpiringPasswordData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayAdmins = useMemo(() => {
    if (!pagination) return allAdminsData;
    const slice = getPaginationSlice(pagination);
    return allAdminsData.slice(slice.startIndex, slice.endIndex);
  }, [allAdminsData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showNo, showComputerIP, showHostname, showUsername, showAccountType, showPasswordExpires].filter(Boolean).length;
  const templateColumns = visibleColumns === 6 
    ? '0.5fr 1fr 1fr 2fr 1.5fr 1.5fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showComputerIP && <div style={headerStyle}>{getDisplayHeader(config, 'IP Address')}</div>}
            {showHostname && <div style={headerStyle}>{getDisplayHeader(config, 'Hostname')}</div>}
            {showUsername && <div style={headerStyle}>{getDisplayHeader(config, 'Username')}</div>}
            {showAccountType && <div style={headerStyle}>{getDisplayHeader(config, 'Account Type')}</div>}
            {showPasswordExpires && <div style={headerStyle}>{getDisplayHeader(config, 'Password Expires')}</div>}
          </div>
          <div className="open-ports-body">
            {displayAdmins.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No admins with non-expiring passwords found.</p>
              </div>
            ) : (
              displayAdmins.map((admin, idx) => {
                const startIndex = pagination && pagination.startIndex !== undefined
                  ? pagination.startIndex
                  : (pagination ? (pagination.currentPage - 1) * pagination.itemsPerPage : 0);
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div className="port-number" style={rowStyleMerged}>{actualNumber}</div>}
                    {showComputerIP && <div className="port-ip" style={rowStyleMerged}>{admin.computer_ip}</div>}
                    {showHostname && <div className="port-ip" style={rowStyleMerged}>{admin.hostname}</div>}
                    {showUsername && <div className="port-service" style={rowStyleMerged}>{admin.username}</div>}
                    {showAccountType && <div className="port-service" style={rowStyleMerged}>{admin.account_type}</div>}
                    {showPasswordExpires && <div className="port-service" style={rowStyleMerged}>{admin.password_expires}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract admins with no expiring password from scan results
 * Matches _Network_Admin_Account_status logic from template2_doc/Network.js
 * Only includes admins where Password Expires is falsy
 */
export function extractAdminsNoExpiringPasswordData(data) {
  const adminsData = [];

  if (!data) return adminsData;

  // Extract from raw scan data
  const scanDataSource = data.scanData || data.data || data;
  const networkDevices = scanDataSource?.network_devices || [];

  networkDevices.forEach(device => {
    // Skip if system is missing or error
    if (!device?.system || device.system === "WMI Error") return;

    const ip = device.ip || '';
    const hostname = device.hostname || device.host || device.name || '';
    const users = device?.system?.['Administrators'];
    if (!Array.isArray(users)) return;

    users.forEach(user => {
      // Match backend logic: only include admins where Password Expires is falsy
      // Backend: if (user['Password Expires']) expire++; else { noexpire_users.push(format); noexpire++; }
      const passwordExpires = user['Password Expires'] !== undefined
        ? user['Password Expires']
        : user['PasswordExpires'];

      // Only include admins with non-expiring passwords (falsy value)
      if (!passwordExpires) {
        adminsData.push({
          computer_ip: ip,
          hostname,
          username: user.Username || user.Name || '',
          account_type: user['Account Type'] || user.AccountType || 'Administrator',
          password_expires: passwordExpires ? 'Yes' : 'No'
        });
      }
    });
  });

  return adminsData;
}

/**
 * Network - Admins with Expiring Password Table
 * Shows admins with passwords that expire
 * Matches _Network_Admin_Account_status logic from template2/Network.js
 * Widget auto-sizes to fit all content
 */
/**
 * Network - Admins with Expiring Password Table
 */
export const NetworkAdminsExpiringPasswordTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'IP Address': 'showComputerIP', 'Hostname': 'showHostname', 'Username': 'showUsername', 'Account Type': 'showAccountType', 'Password Expires': 'showPasswordExpires' });
  const {
    title = 'Admins with Expiring Password',
    showNo = true,
    showComputerIP = true,
    showHostname = true,
    showUsername = true,
    showAccountType = true,
    showPasswordExpires = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'network-admins-expiring-password-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allAdminsData = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractAdminsExpiringPasswordData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayAdmins = useMemo(() => {
    if (!pagination) return allAdminsData;
    const slice = getPaginationSlice(pagination);
    return allAdminsData.slice(slice.startIndex, slice.endIndex);
  }, [allAdminsData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showNo, showComputerIP, showHostname, showUsername, showAccountType, showPasswordExpires].filter(Boolean).length;
  const templateColumns = visibleColumns === 6 
    ? '0.5fr 1fr 1fr 2fr 1.5fr 1.5fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showComputerIP && <div style={headerStyle}>{getDisplayHeader(config, 'IP Address')}</div>}
            {showHostname && <div style={headerStyle}>{getDisplayHeader(config, 'Hostname')}</div>}
            {showUsername && <div style={headerStyle}>{getDisplayHeader(config, 'Username')}</div>}
            {showAccountType && <div style={headerStyle}>{getDisplayHeader(config, 'Account Type')}</div>}
            {showPasswordExpires && <div style={headerStyle}>{getDisplayHeader(config, 'Password Expires')}</div>}
          </div>
          <div className="open-ports-body">
            {displayAdmins.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No admins with expiring passwords found.</p>
              </div>
            ) : (
              displayAdmins.map((admin, idx) => {
                const startIndex = pagination && pagination.startIndex !== undefined
                  ? pagination.startIndex
                  : (pagination ? (pagination.currentPage - 1) * pagination.itemsPerPage : 0);
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div className="port-number" style={rowStyleMerged}>{actualNumber}</div>}
                    {showComputerIP && <div className="port-ip" style={rowStyleMerged}>{admin.computer_ip}</div>}
                    {showHostname && <div className="port-ip" style={rowStyleMerged}>{admin.hostname}</div>}
                    {showUsername && <div className="port-service" style={rowStyleMerged}>{admin.username}</div>}
                    {showAccountType && <div className="port-service" style={rowStyleMerged}>{admin.account_type}</div>}
                    {showPasswordExpires && <div className="port-service" style={rowStyleMerged}>{admin.password_expires}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Network - Admins Used for Daily Work Table
 * Shows admin accounts used for daily work
 * Matches _Network_Admin_Account_status logic from template2/Network.js
 * Widget auto-sizes to fit all content
 */
/**
 * Network - Admins Used for Daily Work Table
 */
export const NetworkAdminsDailyWorkTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'IP Address': 'showComputerIP', 'Hostname': 'showHostname', 'Group': 'showGroup', 'Account': 'showAccount' });
  const {
    title = 'Admins Used for Daily Work',
    showNo = true,
    showComputerIP = true,
    showHostname = true,
    showGroup = true,
    showAccount = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'network-admins-daily-work-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allAdminsData = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractAdminsDailyWorkData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayAdmins = useMemo(() => {
    if (!pagination) return allAdminsData;
    const slice = getPaginationSlice(pagination);
    return allAdminsData.slice(slice.startIndex, slice.endIndex);
  }, [allAdminsData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showNo, showComputerIP, showHostname, showGroup, showAccount].filter(Boolean).length;
  const templateColumns = visibleColumns === 5 
    ? '0.5fr 1.5fr 1.5fr 1.5fr 1.5fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showComputerIP && <div style={headerStyle}>{getDisplayHeader(config, 'IP Address')}</div>}
            {showHostname && <div style={headerStyle}>{getDisplayHeader(config, 'Hostname')}</div>}
            {showGroup && <div style={headerStyle}>{getDisplayHeader(config, 'Group')}</div>}
            {showAccount && <div style={headerStyle}>{getDisplayHeader(config, 'Account')}</div>}
          </div>
          <div className="open-ports-body">
            {displayAdmins.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No admins used for daily work found.</p>
              </div>
            ) : (
              displayAdmins.map((admin, idx) => {
                const startIndex = pagination && pagination.startIndex !== undefined
                  ? pagination.startIndex
                  : (pagination ? (pagination.currentPage - 1) * pagination.itemsPerPage : 0);
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div className="port-number" style={rowStyleMerged}>{actualNumber}</div>}
                    {showComputerIP && <div className="port-ip" style={rowStyleMerged}>{admin.computer_ip}</div>}
                    {showHostname && <div className="port-ip" style={rowStyleMerged}>{admin.hostname}</div>}
                    {showGroup && <div className="port-service" style={rowStyleMerged}>{admin.group}</div>}
                    {showAccount && <div className="port-service" style={rowStyleMerged}>{admin.account}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract admins with expiring password from scan results
 * Matches _Network_Admin_Account_status logic from template2/Network.js
 * Only includes admins where Password Expires is truthy
 */
export function extractAdminsExpiringPasswordData(data) {
  const adminsData = [];

  if (!data) return adminsData;

  // Extract from raw scan data
  const scanDataSource = data.scanData || data.data || data;
  const networkDevices = scanDataSource?.network_devices || [];

  networkDevices.forEach(device => {
    // Skip if system is missing or error
    if (!device?.system || device.system === "WMI Error") return;

    const ip = device.ip || '';
    const hostname = device.hostname || device.host || device.name || '';
    const users = device?.system?.['Administrators'];
    if (!Array.isArray(users)) return;

    users.forEach(user => {
      // Match backend logic: only include admins where Password Expires is truthy
      // Backend: if (user['Password Expires']) expire++;
      const passwordExpires = user['Password Expires'] !== undefined
        ? user['Password Expires']
        : user['PasswordExpires'];

      // Only include admins with expiring passwords (truthy value)
      if (passwordExpires) {
        adminsData.push({
          computer_ip: ip,
          hostname,
          username: user.Username || user.Name || '',
          account_type: user['Account Type'] || user.AccountType || 'Administrator',
          password_expires: passwordExpires ? 'Yes' : 'No'
        });
      }
    });
  });

  return adminsData;
}

/**
 * Extract admins used for daily work from scan results
 * Matches _Network_Admin_Account_status logic from template2/Network.js
 * Uses regular_admins array: adminReg.push([ip, admin.Group, admin.Account])
 */
export function extractAdminsDailyWorkData(data) {
  const adminsData = [];

  if (!data) return adminsData;

  // Extract from raw scan data
  const scanDataSource = data.scanData || data.data || data;
  const networkDevices = scanDataSource?.network_devices || [];

  networkDevices.forEach(device => {
    // Skip if system is missing or error
    if (!device?.system || device.system === "WMI Error") return;

    const ip = device.ip || '';
    const hostname = device.hostname || device.host || device.name || '';
    const regularAdmins = device?.system?.['regular_admins'];
    if (!Array.isArray(regularAdmins)) return;

    // Match backend logic: adminReg.push([ip, admin.Group, admin.Account])
    regularAdmins.forEach(admin => {
      adminsData.push({
        computer_ip: ip,
        hostname,
        group: admin.Group || admin.group || '',
        account: admin.Account || admin.account || ''
      });
    });
  });

  return adminsData;
}

/**
 * Network - Users with No Expiring Password Table
 * Shows users with passwords that don't expire
 * Matches _Network_Account_status logic from template2_doc/Network.js
 * Widget auto-sizes to fit all content
 */
/**
 * Network - Users with No Expiring Password Table
 */
export const NetworkUsersNoExpiringPasswordTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'IP Address': 'showComputerIP', 'Hostname': 'showHostname', 'Username': 'showUsername', 'Account Type': 'showAccountType', 'Password Expires': 'showPasswordExpires' });
  const {
    title = 'Users with no Expiring Password',
    showNo = true,
    showComputerIP = true,
    showHostname = true,
    showUsername = true,
    showAccountType = true,
    showPasswordExpires = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'network-users-no-expiring-password-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allUsersData = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractUsersNoExpiringPasswordData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayUsers = useMemo(() => {
    if (!pagination) return allUsersData;
    const slice = getPaginationSlice(pagination);
    return allUsersData.slice(slice.startIndex, slice.endIndex);
  }, [allUsersData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showNo, showComputerIP, showHostname, showUsername, showAccountType, showPasswordExpires].filter(Boolean).length;
  const templateColumns = visibleColumns === 6 
    ? '0.5fr 1fr 1fr 2fr 1.5fr 1.5fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showComputerIP && <div style={headerStyle}>{getDisplayHeader(config, 'IP Address')}</div>}
            {showHostname && <div style={headerStyle}>{getDisplayHeader(config, 'Hostname')}</div>}
            {showUsername && <div style={headerStyle}>{getDisplayHeader(config, 'Username')}</div>}
            {showAccountType && <div style={headerStyle}>{getDisplayHeader(config, 'Account Type')}</div>}
            {showPasswordExpires && <div style={headerStyle}>{getDisplayHeader(config, 'Password Expires')}</div>}
          </div>
          <div className="open-ports-body">
            {displayUsers.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No users with non-expiring passwords found.</p>
              </div>
            ) : (
              displayUsers.map((user, idx) => {
                const startIndex = pagination && pagination.startIndex !== undefined
                  ? pagination.startIndex
                  : (pagination ? (pagination.currentPage - 1) * pagination.itemsPerPage : 0);
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div className="port-number" style={rowStyleMerged}>{actualNumber}</div>}
                    {showComputerIP && <div className="port-ip" style={rowStyleMerged}>{user.computer_ip}</div>}
                    {showHostname && <div className="port-ip" style={rowStyleMerged}>{user.hostname}</div>}
                    {showUsername && <div className="port-service" style={rowStyleMerged}>{user.username}</div>}
                    {showAccountType && <div className="port-service" style={rowStyleMerged}>{user.account_type}</div>}
                    {showPasswordExpires && <div className="port-service" style={rowStyleMerged}>{user.password_expires}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Network - Users with Expiring Password Table
 * Shows users with passwords that expire
 * Matches _Network_Account_status logic from template2_doc/Network.js
 * Widget auto-sizes to fit all content
 */
/**
 * Network - Users with Expiring Password Table
 */
export const NetworkUsersExpiringPasswordTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'IP Address': 'showComputerIP', 'Hostname': 'showHostname', 'Username': 'showUsername', 'Account Type': 'showAccountType', 'Password Expires': 'showPasswordExpires' });
  const {
    title = 'Users with Expiring Password',
    showNo = true,
    showComputerIP = true,
    showHostname = true,
    showUsername = true,
    showAccountType = true,
    showPasswordExpires = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'network-users-expiring-password-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allUsersData = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractUsersExpiringPasswordData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayUsers = useMemo(() => {
    if (!pagination) return allUsersData;
    const slice = getPaginationSlice(pagination);
    return allUsersData.slice(slice.startIndex, slice.endIndex);
  }, [allUsersData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showNo, showComputerIP, showHostname, showUsername, showAccountType, showPasswordExpires].filter(Boolean).length;
  const templateColumns = visibleColumns === 6 
    ? '0.5fr 1fr 1fr 2fr 1.5fr 1.5fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showComputerIP && <div style={headerStyle}>{getDisplayHeader(config, 'IP Address')}</div>}
            {showHostname && <div style={headerStyle}>{getDisplayHeader(config, 'Hostname')}</div>}
            {showUsername && <div style={headerStyle}>{getDisplayHeader(config, 'Username')}</div>}
            {showAccountType && <div style={headerStyle}>{getDisplayHeader(config, 'Account Type')}</div>}
            {showPasswordExpires && <div style={headerStyle}>{getDisplayHeader(config, 'Password Expires')}</div>}
          </div>
          <div className="open-ports-body">
            {displayUsers.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No users with expiring passwords found.</p>
              </div>
            ) : (
              displayUsers.map((user, idx) => {
                const startIndex = pagination && pagination.startIndex !== undefined
                  ? pagination.startIndex
                  : (pagination ? (pagination.currentPage - 1) * pagination.itemsPerPage : 0);
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div className="port-number" style={rowStyleMerged}>{actualNumber}</div>}
                    {showComputerIP && <div className="port-ip" style={rowStyleMerged}>{user.computer_ip}</div>}
                    {showHostname && <div className="port-ip" style={rowStyleMerged}>{user.hostname}</div>}
                    {showUsername && <div className="port-service" style={rowStyleMerged}>{user.username}</div>}
                    {showAccountType && <div className="port-service" style={rowStyleMerged}>{user.account_type}</div>}
                    {showPasswordExpires && <div className="port-service" style={rowStyleMerged}>{user.password_expires}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Extract users with no expiring password from scan results
 * Matches _Network_Account_status logic from template2_doc/Network.js
 * Only includes users where Password Expires is falsy
 */
export function extractUsersNoExpiringPasswordData(data) {
  const usersData = [];

  if (!data) return usersData;

  // Extract from raw scan data
  const scanDataSource = data.scanData || data.data || data;
  const networkDevices = scanDataSource?.network_devices || [];

  networkDevices.forEach(device => {
    // Skip if system is missing or error
    if (!device?.system || device.system === "WMI Error") return;

    const ip = device.ip || '';
    const hostname = device.hostname || device.host || device.name || '';
    const users = device?.system?.['Local Users'];
    if (!Array.isArray(users)) return;

    users.forEach(user => {
      // Match backend logic: only include users where Password Expires is falsy
      // Backend: if (user['Password Expires']) expire++; else { noexpire_users.push(format); noexpire++; }
      const passwordExpires = user['Password Expires'] !== undefined
        ? user['Password Expires']
        : user['PasswordExpires'];

      // Only include users with non-expiring passwords (falsy value)
      if (!passwordExpires) {
        usersData.push({
          computer_ip: ip,
          hostname,
          username: user.Username || user.Name || '',
          account_type: user['Account Type'] || user.AccountType || 'Standard',
          password_expires: passwordExpires ? 'Yes' : 'No'
        });
      }
    });
  });

  return usersData;
}

/**
 * Extract users with expiring password from scan results
 * Matches _Network_Account_status logic from template2_doc/Network.js
 * Only includes users where Password Expires is truthy
 */
export function extractUsersExpiringPasswordData(data) {
  const usersData = [];

  if (!data) return usersData;

  // Extract from raw scan data
  const scanDataSource = data.scanData || data.data || data;
  const networkDevices = scanDataSource?.network_devices || [];

  networkDevices.forEach(device => {
    // Skip if system is missing or error
    if (!device?.system || device.system === "WMI Error") return;

    const ip = device.ip || '';
    const hostname = device.hostname || device.host || device.name || '';
    const users = device?.system?.['Local Users'];
    if (!Array.isArray(users)) return;

    users.forEach(user => {
      // Match backend logic: only include users where Password Expires is truthy
      // Backend: if (user['Password Expires']) expire++;
      const passwordExpires = user['Password Expires'] !== undefined
        ? user['Password Expires']
        : user['PasswordExpires'];

      // Only include users with expiring passwords (truthy value)
      if (passwordExpires) {
        usersData.push({
          computer_ip: ip,
          hostname,
          username: user.Username || user.Name || '',
          account_type: user['Account Type'] || user.AccountType || 'Standard',
          password_expires: passwordExpires ? 'Yes' : 'No'
        });
      }
    });
  });

  return usersData;
}

/**
 * Analyze Network Services status
 * Matches _Network_Services_status from template2/Network.js:1027-1102
 */
function analyzeNetworkServices(devices) {
  const rows = [];
  const suspicious = [];
  let device_suspicious = new Set();
  const all = {};
  const total = devices.length || 0;
  const detail = [];

  try {
    for (const device of devices) {
      const ip = device['ip'] || '';
      const hostname = device['hostname'] || device['host'] || device['name'] || '';
      const services = device?.system?.['Non-Default Services'];
      if (!Array.isArray(services)) continue;

      for (const service of services) {
        const account = service['Start Account'];
        const state = service['State'];
        const startMode = service['Start Mode'];
        const DN = service['Display Name'];
        const name = service['Name'] || '';
        let risk = 'Low';
        let note = 'Standard non-running service';

        // Count service state
        all[state] = (all[state] || 0) + 1;

        // Only inspect high-privilege accounts
        if (["localSystem", "Administrator"].includes(account)) {
          // Risk assessment
          if (state === 'Running' && startMode === 'Auto') {
            risk = 'High';
            note = 'Running with auto-start and high privileges';
            suspicious.push([
              ip,
              hostname,
              name,
              DN,
              'High privilege service account',
              risk,
              note
            ]);
            device_suspicious.add(ip);
          }
        }

        detail.push([
          ip,
          hostname,
          name,
          DN,
          ["localSystem", "Administrator"].includes(account)
            ? `High privilege service account :${account}`
            : `Running as ${account} account`,
          state,
          startMode
        ]);
      }
    }

    // Build rows with severity tags
    Object.keys(all).forEach(key => {
      let severity = 'Low';
      let explanation = 'Unknown or inactive state — treated as low risk.';
      if (key === 'Running') {
        severity = 'Critical';
        explanation = 'Service is currently running. If combined with high privileges or auto-start, it may present an active risk.';
      } else if (key === 'Stopped') {
        severity = 'Moderate';
        explanation = 'Service is not running but can be started manually. May still pose a risk depending on privilege and access.';
      } else if (key === 'Disabled') {
        severity = 'Low';
        explanation = 'Service is disabled and cannot start unless explicitly re-enabled. Minimal security concern.';
      }
      rows.push([key, all[key], severity, explanation]);
    });
  } catch (err) {
    console.log("Error analyzeNetworkServices", err);
    return null;
  }

  const suspicious_devices = device_suspicious.size;
  const avg = total > 0 ? Math.round(100 * suspicious_devices / total) : 0;

  // Final return: [rows, suspicious, total, detail, avg, suspicious_devices]
  return [rows, suspicious, total, detail, avg, suspicious_devices];
}

/**
 * Extract detailed non-default services data from scan results
 * Returns array of { ip, name, displayName, reason, state, startMode }
 */
export function extractDetailedServicesData(data) {
  const servicesData = [];

  if (!data) return servicesData;

  // Extract from raw scan data
  const scanDataSource = data.scanData || data.data || data;
  const networkDevices = scanDataSource?.network_devices || [];

  // Filter to only devices that have system data
  const validDevices = networkDevices.filter(device =>
    device?.system && device.system !== "WMI Error"
  );

  const result = analyzeNetworkServices(validDevices);
  if (!result) return servicesData;

  const [, , , detail] = result;

  return detail.map(row => ({
    ip: row[0],
    hostname: row[1],
    name: row[2],
    displayName: row[3],
    reason: row[4],
    state: row[5],
    startMode: row[6]
  }));
}

/**
 * Extract services risk breakdown by state data from scan results
 * Returns array of { state, totalServices, severity, explanation }
 */
export function extractServicesRiskBreakdownData(data) {
  const breakdownData = [];

  if (!data) return breakdownData;

  // Extract from raw scan data
  const scanDataSource = data.scanData || data.data || data;
  const networkDevices = scanDataSource?.network_devices || [];

  // Filter to only devices that have system data
  const validDevices = networkDevices.filter(device =>
    device?.system && device.system !== "WMI Error"
  );

  const result = analyzeNetworkServices(validDevices);
  if (!result) return breakdownData;

  const [rows] = result;

  return rows.map(row => ({
    state: row[0],
    totalServices: row[1],
    severity: row[2],
    explanation: row[3]
  }));
}

/**
 * Extract high-privilege services data from scan results
 * Returns array of { ip, name, displayName, reason, risk, note }
 */
export function extractHighPrivilegeServicesData(data) {
  const servicesData = [];

  if (!data) return servicesData;

  // Extract from raw scan data
  const scanDataSource = data.scanData || data.data || data;
  const networkDevices = scanDataSource?.network_devices || [];

  // Filter to only devices that have system data
  const validDevices = networkDevices.filter(device =>
    device?.system && device.system !== "WMI Error"
  );

  const result = analyzeNetworkServices(validDevices);
  if (!result) return servicesData;

  const [, suspicious] = result;

  return suspicious.map(row => ({
    ip: row[0],
    hostname: row[1],
    name: row[2],
    displayName: row[3],
    reason: row[4],
    risk: row[5],
    note: row[6]
  }));
}

/**
 * Network Detailed Non-Default Services Found Table Widget
 * Shows all non-default services with details
 * Matches Network_Services_status table from template2/Network.js:1128-1140
 * Widget auto-sizes to fit all content
 */
/**
 * Network - Detailed Non-Default Services Found Table
 */
export const NetworkDetailedServicesTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'IP Address': 'showIP', 'Hostname': 'showHostname', 'Name': 'showName', 'Display Name': 'showDisplayName', 'Reason': 'showReason', 'State': 'showState', 'Start Mode': 'showStartMode' });
  const {
    title = 'Detailed Non-Default Services Found',
    showNo = true,
    showIP = true,
    showHostname = true,
    showName = true,
    showDisplayName = true,
    showReason = true,
    showState = true,
    showStartMode = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'network-detailed-services-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allServicesData = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractDetailedServicesData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayServices = useMemo(() => {
    if (!pagination) return allServicesData;
    const slice = getPaginationSlice(pagination);
    return allServicesData.slice(slice.startIndex, slice.endIndex);
  }, [allServicesData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showNo, showIP, showHostname, showName, showDisplayName, showReason, showState, showStartMode].filter(Boolean).length;
  const templateColumns = visibleColumns === 8 
    ? '0.5fr 1fr 1fr 1.5fr 2fr 2.5fr 1fr 1fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showIP && <div style={headerStyle}>{getDisplayHeader(config, 'IP Address')}</div>}
            {showHostname && <div style={headerStyle}>{getDisplayHeader(config, 'Hostname')}</div>}
            {showName && <div style={headerStyle}>{getDisplayHeader(config, 'Name')}</div>}
            {showDisplayName && <div style={headerStyle}>{getDisplayHeader(config, 'Display Name')}</div>}
            {showReason && <div style={headerStyle}>{getDisplayHeader(config, 'Reason')}</div>}
            {showState && <div style={headerStyle}>{getDisplayHeader(config, 'State')}</div>}
            {showStartMode && <div style={headerStyle}>{getDisplayHeader(config, 'Start Mode')}</div>}
          </div>
          <div className="open-ports-body">
            {displayServices.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No non-default services found.</p>
              </div>
            ) : (
              displayServices.map((service, idx) => {
                const stateColors = {
                  'Running': theme?.statusColors?.red || '#d71920',
                  'Stopped': theme?.statusColors?.yellow || '#fdbc13',
                  'Disabled': theme?.statusColors?.green || '#6fca42'
                };
                const stateColor = stateColors[service.state] || '#666';
                const startIndex = pagination && pagination.startIndex !== undefined
                  ? pagination.startIndex
                  : (pagination ? (pagination.currentPage - 1) * pagination.itemsPerPage : 0);
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div className="port-number" style={rowStyleMerged}>{actualNumber}</div>}
                    {showIP && <div className="port-ip" style={rowStyleMerged}>{service.ip}</div>}
                    {showHostname && <div className="port-ip" style={rowStyleMerged}>{service.hostname}</div>}
                    {showName && <div className="port-service" style={rowStyleMerged}>{service.name}</div>}
                    {showDisplayName && <div className="port-service" style={rowStyleMerged}>{service.displayName}</div>}
                    {showReason && <div className="port-service" style={rowStyleMerged}>{service.reason}</div>}
                    {showState && (
                      <div style={rowStyleMerged}>
                        <span style={{ color: stateColor, fontWeight: 'bold' }}>
                          {service.state}
                        </span>
                      </div>
                    )}
                    {showStartMode && <div className="port-service" style={rowStyleMerged}>{service.startMode}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Network Non-Default Services Risk Breakdown by State Table Widget
 * Shows risk breakdown by service state
 * Matches Network_Services_status table from template2/Network.js:1142-1154
 * Widget auto-sizes to fit all content
 */
/**
 * Network - Non-Default Services Risk Breakdown by State Table
 */
export const NetworkServicesRiskBreakdownTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'State': 'showState', 'Total Services': 'showTotalServices', 'Severity': 'showSeverity', 'Explanation': 'showExplanation' });
  const {
    title = 'Non-Default Services Risk Breakdown by State',
    showState = true,
    showTotalServices = true,
    showSeverity = true,
    showExplanation = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'network-services-risk-breakdown-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allBreakdownData = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractServicesRiskBreakdownData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayBreakdown = useMemo(() => {
    if (!pagination) return allBreakdownData;
    const slice = getPaginationSlice(pagination);
    return allBreakdownData.slice(slice.startIndex, slice.endIndex);
  }, [allBreakdownData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showState, showTotalServices, showSeverity, showExplanation].filter(Boolean).length;
  const templateColumns = visibleColumns === 4 
    ? '1.5fr 1.5fr 1fr 2fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showState && <div style={headerStyle}>{getDisplayHeader(config, 'State')}</div>}
            {showTotalServices && <div style={headerStyle}>{getDisplayHeader(config, 'Total Services')}</div>}
            {showSeverity && <div style={headerStyle}>{getDisplayHeader(config, 'Severity')}</div>}
            {showExplanation && <div style={headerStyle}>{getDisplayHeader(config, 'Explanation')}</div>}
          </div>
          <div className="open-ports-body">
            {displayBreakdown.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No service risk breakdown data available.</p>
              </div>
            ) : (
              displayBreakdown.map((breakdown, idx) => {
                const severityColors = {
                  'Critical': theme?.statusColors?.red || '#d71920',
                  'Moderate': theme?.statusColors?.yellow || '#fdbc13',
                  'Low': theme?.statusColors?.green || '#6fca42'
                };
                const severityColor = severityColors[breakdown.severity] || '#666';

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showState && <div className="port-ip" style={rowStyleMerged}>{breakdown.state}</div>}
                    {showTotalServices && <div className="port-number" style={rowStyleMerged}>{breakdown.totalServices}</div>}
                    {showSeverity && (
                      <div style={rowStyleMerged}>
                        <span style={{ color: severityColor, fontWeight: 'bold' }}>
                          {breakdown.severity}
                        </span>
                      </div>
                    )}
                    {showExplanation && <div className="port-service" style={rowStyleMerged}>{breakdown.explanation}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

/**
 * Network High-Privilege Services Table Widget
 * Shows high-privilege services with risk assessment
 * Matches Network_Services_status table from template2/Network.js:1155-1167
 * Widget auto-sizes to fit all content
 */
/**
 * Network - High-Privilege Services Table
 */
export const NetworkHighPrivilegeServicesTableWidget = ({ data, config = {}, style = {}, pagination, theme }) => {
  const hiddenOverrides = applyHiddenColumns(config, { 'No.': 'showNo', 'IP Address': 'showIP', 'Hostname': 'showHostname', 'Name': 'showName', 'Display Name': 'showDisplayName', 'Reason': 'showReason', 'Risk': 'showRisk', 'Note': 'showNote' });
  const {
    title = 'High-Privilege Services',
    showNo = true,
    showIP = true,
    showHostname = true,
    showName = true,
    showDisplayName = true,
    showReason = true,
    showRisk = true,
    showNote = true,
    ...restConfig
  } = { ...config, ...hiddenOverrides };

  const widgetType = 'network-high-privilege-services-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  const allHighPrivilegeData = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractHighPrivilegeServicesData(data), config.tableFilters || config?.tableFilters),
      config.tableSort
    );
  }, [data, config.tableFilters, config.tableSort]);

  const displayServices = useMemo(() => {
    if (!pagination) return allHighPrivilegeData;
    const slice = getPaginationSlice(pagination);
    return allHighPrivilegeData.slice(slice.startIndex, slice.endIndex);
  }, [allHighPrivilegeData, pagination]);

  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left'
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  const visibleColumns = [showNo, showIP, showHostname, showName, showDisplayName, showReason, showRisk, showNote].filter(Boolean).length;
  const templateColumns = visibleColumns === 8 
    ? '0.5fr 1.5fr 1.5fr 1.5fr 2fr 2fr 1fr 2fr'
    : Array(visibleColumns).fill('1fr').join(' ');

  return (
    <div className="open-ports-widget" style={style}>
      {title && <h3 className="findings-widget-title" style={titleStyle}>{title}</h3>}
      <div className="open-ports-table">
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {showNo && <div style={headerStyle}>{getDisplayHeader(config, 'No.')}</div>}
            {showIP && <div style={headerStyle}>{getDisplayHeader(config, 'IP Address')}</div>}
            {showHostname && <div style={headerStyle}>{getDisplayHeader(config, 'Hostname')}</div>}
            {showName && <div style={headerStyle}>{getDisplayHeader(config, 'Name')}</div>}
            {showDisplayName && <div style={headerStyle}>{getDisplayHeader(config, 'Display Name')}</div>}
            {showReason && <div style={headerStyle}>{getDisplayHeader(config, 'Reason')}</div>}
            {showRisk && <div style={headerStyle}>{getDisplayHeader(config, 'Risk')}</div>}
            {showNote && <div style={headerStyle}>{getDisplayHeader(config, 'Note')}</div>}
          </div>
          <div className="open-ports-body">
            {displayServices.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <p style={{ fontStyle: 'italic', margin: 0 }}>No high-privilege services found.</p>
              </div>
            ) : (
              displayServices.map((service, idx) => {
                const riskColors = {
                  'High': theme?.statusColors?.red || '#d71920',
                  'Moderate': theme?.statusColors?.yellow || '#fdbc13',
                  'Low': theme?.statusColors?.green || '#6fca42'
                };
                const riskColor = riskColors[service.risk] || '#666';
                const startIndex = pagination && pagination.startIndex !== undefined
                  ? pagination.startIndex
                  : (pagination ? (pagination.currentPage - 1) * pagination.itemsPerPage : 0);
                const actualNumber = startIndex + idx + 1;

                return (
                  <div key={idx} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleProp }}>
                    {showNo && <div className="port-number" style={rowStyleMerged}>{actualNumber}</div>}
                    {showIP && <div className="port-ip" style={rowStyleMerged}>{service.ip}</div>}
                    {showHostname && <div className="port-ip" style={rowStyleMerged}>{service.hostname}</div>}
                    {showName && <div className="port-service" style={rowStyleMerged}>{service.name}</div>}
                    {showDisplayName && <div className="port-service" style={rowStyleMerged}>{service.displayName}</div>}
                    {showReason && <div className="port-service" style={rowStyleMerged}>{service.reason}</div>}
                    {showRisk && (
                      <div style={rowStyleMerged}>
                        <span style={{ color: riskColor, fontWeight: 'bold' }}>
                          {service.risk}
                        </span>
                      </div>
                    )}
                    {showNote && <div className="port-service" style={rowStyleMerged}>{service.note}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
    </div>
  );
};

// Due to file size, I'll continue with Localhost, MS365, HIBP, and PII widgets in the next part
// This comment ensures the file is valid JavaScript
